import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from './components/layouts/DashboardLayout';
import {
  UserCheck,
  Users,
  Plus,
  FileText,
  Calendar,
  MapPin,
  Clock,
  CheckCircle2,
  AlertCircle,
  Upload,
  CreditCard,
  Building2,
  Award,
  ChevronRight,
  X,
  RefreshCw,
  Tag,
  ShieldCheck,
  BookOpen,
  Printer,
  Edit3,
  ExternalLink,
  Eye,
  Info,
  Phone,
  HelpCircle,
  Sparkles,
  Heart,
  School,
  GraduationCap,
  CalendarClock,
  ArrowRight,
  MessageSquare,
  Download
} from 'lucide-react';
import schoolLogo from './assets/logo/logo_phs.png';
import {
  LetterOfAcceptanceModal,
  OfficialEnrollmentCertificateModal,
  OfficialPaymentReceiptModal,
  ExaminationPassModal
} from './components/admissions/EnglishAdmissionsDocuments';
import ModernAdmissionsToast from './components/admissions/ModernAdmissionsToast';
import './ParentDashboard.css';

const formatCurrency = (val) => {
  const num = Number(val || 0);
  return 'Rp ' + num.toLocaleString('id-ID');
};

const formatDateId = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('id-ID', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (_) {
    return dateStr;
  }
};

function ParentDashboard({ user, onLogout, apiBase }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [parentProfile, setParentProfile] = useState(user || {});
  const [children, setChildren] = useState([]);
  const [portalConfig, setPortalConfig] = useState({
    levels: [],
    periods: [],
    active_period: null,
    campuses: [
      { code: 'pos_pengumben', label: 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13' },
      { code: 'srengseng', label: 'Campus 2 (Srengseng) - Jl. Srengseng Raya No.69' },
    ],
    intake_types: ['Online Website', 'Open House', 'Walk in Parent'],
  });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [modalFeedback, setModalFeedback] = useState(null);

  // Modals state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [pendingAddChildAfterProfile, setPendingAddChildAfterProfile] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [activeChildDetail, setActiveChildDetail] = useState(null);
  const [receiptModalChild, setReceiptModalChild] = useState(null);
  const [loaModalChild, setLoaModalChild] = useState(null);
  const [enrollmentCertChild, setEnrollmentCertChild] = useState(null);
  const [testCardModalChild, setTestCardModalChild] = useState(null);

  // Tablet Walk-in / Kiosk Mode Isolation
  const isKioskMode = typeof window !== 'undefined' && (
    window.location.search.includes('mode=kiosk') ||
    window.location.search.includes('kiosk=1') ||
    localStorage.getItem('ph_kiosk_mode') === 'true'
  );

  const handleKioskExit = () => {
    if (window.confirm('Selesaikan sesi walk-in? Sesi login Anda akan dihapus secara menyeluruh demi keamanan agar pendaftar berikutnya tidak dapat melihat data keluarga Anda.')) {
      localStorage.removeItem('ph_auth_token');
      localStorage.removeItem('auth_token');
      sessionStorage.clear();
      window.location.href = '/login?mode=kiosk';
    }
  };

  // Helper to update feedback toast notification
  const updateModalFeedback = (fb) => {
    setModalFeedback(null);
    if (fb) {
      setMessage(fb);
    }
  };

  // Parent Profile Form (One-time setup for all children)
  const [profileForm, setProfileForm] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    father_name: user?.father_name || '',
    father_phone: user?.father_phone || '',
    father_occupation: user?.father_occupation || '',
    father_nationality: user?.father_nationality || 'Indonesian (WNI)',
    mother_name: user?.mother_name || '',
    mother_phone: user?.mother_phone || '',
    mother_occupation: user?.mother_occupation || '',
    mother_nationality: user?.mother_nationality || 'Indonesian (WNI)',
    guardian_name: user?.guardian_name || '',
    guardian_phone: user?.guardian_phone || '',
    home_address: user?.home_address || '',
    city: user?.city || 'Jakarta Barat',
    postal_code: user?.postal_code || '',
    parent_ktp_url: user?.parent_ktp_url || '',
    family_card_url: user?.family_card_url || '',
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Child Registration Form (Synchronized with Physical Registration Form)
  const [childForm, setChildForm] = useState({
    campus_location: 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13',
    grade_target: '',
    intake_type: 'Online Website',
    joint_date: '',
    child_full_name: '',
    birth_place: '',
    birth_date: '',
    gender: 'Male',
    nationality: 'Indonesian (WNI)',
    previous_school: '',
    promo_code: '',
    documents: {
      birth_certificate: '',
      child_photo: '',
      report_card: '',
    },
  });
  const [promoCheck, setPromoCheck] = useState({ loading: false, valid: false, info: null, error: '' });
  const [submittingChild, setSubmittingChild] = useState(false);
  const [uploadingField, setUploadingField] = useState('');
  const [uploadProgress, setUploadProgress] = useState({}); // { [fieldKey]: percentage (0-100) }
  const [uploadedFilesMeta, setUploadedFilesMeta] = useState({}); // { [fieldKey]: { name, size, formattedSize, time, url } }

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Document Revision Re-upload state
  const [revisionDocs, setRevisionDocs] = useState({});
  const [submittingRevision, setSubmittingRevision] = useState(false);

  // Payment Submission state
  const [paymentForm, setPaymentForm] = useState({
    payment_method: 'transfer_bank',
    payment_amount_paid: '',
    payment_proof_url: '',
    sender_account_name: '',
    sender_bank_name: 'Bank BCA',
    additional_notes: '',
    payment_notes: '',
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);

  const getAuthHeaders = useCallback((isJson = true) => {
    const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token');
    const headers = {};
    if (isJson) {
      headers['Content-Type'] = 'application/json';
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, []);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, appsRes] = await Promise.all([
        fetch(`${apiBase}/admissions/portal-config`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        }),
        fetch(`${apiBase}/admissions/applications/mine`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        }),
      ]);

      const cfgData = await cfgRes.json().catch(() => null);
      if (cfgRes.ok && cfgData?.ok && cfgData.data) {
        setPortalConfig((prev) => ({
          ...prev,
          ...cfgData.data,
        }));
      }

      const appsData = await appsRes.json().catch(() => null);
      if (appsRes.ok && appsData?.ok) {
        setChildren(appsData.data || []);
        if (appsData.parent_profile) {
          setParentProfile(appsData.parent_profile);
          setProfileForm({
            full_name: appsData.parent_profile.full_name || '',
            phone: appsData.parent_profile.phone || '',
            father_name: appsData.parent_profile.father_name || '',
            father_phone: appsData.parent_profile.father_phone || '',
            father_occupation: appsData.parent_profile.father_occupation || '',
            father_nationality: appsData.parent_profile.father_nationality || 'Indonesian (WNI)',
            mother_name: appsData.parent_profile.mother_name || '',
            mother_phone: appsData.parent_profile.mother_phone || '',
            mother_occupation: appsData.parent_profile.mother_occupation || '',
            mother_nationality: appsData.parent_profile.mother_nationality || 'Indonesian (WNI)',
            guardian_name: appsData.parent_profile.guardian_name || '',
            guardian_phone: appsData.parent_profile.guardian_phone || '',
            home_address: appsData.parent_profile.home_address || '',
            city: appsData.parent_profile.city || 'Jakarta Barat',
            postal_code: appsData.parent_profile.postal_code || '',
            parent_ktp_url: appsData.parent_profile.parent_ktp_url || '',
            family_card_url: appsData.parent_profile.family_card_url || '',
          });
        }
      }
    } catch (err) {
      console.error('Error loading parent portal data:', err);
    } finally {
      setLoading(false);
    }
  }, [apiBase, getAuthHeaders]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Keep activeChildDetail synced after reload
  useEffect(() => {
    if (activeChildDetail) {
      const updated = children.find((c) => c.id === activeChildDetail.id);
      if (updated) {
        setActiveChildDetail(updated);
      }
    }
  }, [children, activeChildDetail]);

  // Auto-dismiss message notification after 15 seconds
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => {
      setMessage(null);
    }, 15000);
    return () => clearTimeout(timer);
  }, [message]);

  // Clear message on tab navigation
  useEffect(() => {
    setMessage(null);
  }, [activeTab]);

  const handleFileUpload = async (file, fieldKey, onComplete) => {
    if (!file) return;

    // Strict format validation: explicitly reject spreadsheets/Excel and allow only PDF/Images
    const allowedExts = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
    const ext = (file.name || '').split('.').pop().toLowerCase();
    const forbiddenExts = ['xls', 'xlsx', 'csv', 'ods', 'doc', 'docx', 'txt', 'zip', 'rar'];

    if (forbiddenExts.includes(ext) || !allowedExts.includes(ext)) {
      setMessage({
        type: 'error',
        text: `Dokumen dengan ekstensi .${ext.toUpperCase() || 'tidak valid'} tidak diizinkan. Sistem hanya menerima file PDF (.pdf) dan foto/gambar (.jpg, .jpeg, .png, .webp). File spreadsheet/Excel tidak diperbolehkan.`,
      });
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      setMessage({
        type: 'error',
        text: 'Ukuran file terlalu besar. Maksimal 15 MB.',
      });
      return;
    }

    setUploadingField(fieldKey);
    setUploadProgress((prev) => ({ ...prev, [fieldKey]: 5 }));

    try {
      // Use XMLHttpRequest for real-time progress percentage tracking
      const uploadPromise = new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.withCredentials = true;

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percent = Math.min(99, Math.max(5, Math.round((e.loaded / e.total) * 100)));
            setUploadProgress((prev) => ({ ...prev, [fieldKey]: percent }));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const resJson = JSON.parse(xhr.responseText);
              resolve(resJson);
            } catch (err) {
              reject(new Error('Format balasan server tidak valid'));
            }
          } else {
            try {
              const errJson = JSON.parse(xhr.responseText);
              reject(new Error(errJson.message || `Upload gagal dengan kode status ${xhr.status}`));
            } catch (_) {
              reject(new Error(`Upload gagal dengan kode status ${xhr.status}`));
            }
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Koneksi terputus saat mengunggah')));
        xhr.addEventListener('abort', () => reject(new Error('Unggahan dibatalkan')));

        const formData = new FormData();
        formData.append('file', file);

        xhr.open('POST', `${apiBase}/admissions/upload-file`, true);
        const headers = getAuthHeaders(false);
        Object.entries(headers || {}).forEach(([k, v]) => {
          xhr.setRequestHeader(k, v);
        });
        xhr.send(formData);
      });

      let data;
      try {
        data = await uploadPromise;
      } catch (uploadErr) {
        // Fallback base64 upload if FormData is rejected
        setUploadProgress((prev) => ({ ...prev, [fieldKey]: 40 }));
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        setUploadProgress((prev) => ({ ...prev, [fieldKey]: 75 }));
        const res = await fetch(`${apiBase}/admissions/upload-file`, {
          method: 'POST',
          credentials: 'include',
          headers: getAuthHeaders(true),
          body: JSON.stringify({
            file_data: base64,
            file_name: file.name,
          }),
        });
        data = await res.json().catch(() => null);
      }

      if (data?.ok && data.url) {
        setUploadProgress((prev) => ({ ...prev, [fieldKey]: 100 }));
        const fileMeta = {
          name: file.name,
          size: file.size,
          formattedSize: formatFileSize(file.size),
          time: new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }),
          url: data.url,
        };
        setUploadedFilesMeta((prev) => ({ ...prev, [fieldKey]: fileMeta }));
        onComplete(data.url, fileMeta);
        setMessage({
          type: 'success',
          text: `File "${file.name}" (${formatFileSize(file.size)}) berhasil diunggah!`,
        });
      } else {
        setMessage({
          type: 'error',
          text: data?.message || 'Gagal mengunggah file. Pastikan format JPG, PNG, atau PDF (maks 15MB).',
        });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Kesalahan jaringan saat mengunggah file.' });
    } finally {
      setUploadingField('');
    }
  };

  // Click "+ Daftarkan Anak Baru" -> Check if Parent Profile is complete first!
  const handleStartAddChild = () => {
    if (!parentProfile?.is_profile_complete) {
      setPendingAddChildAfterProfile(true);
      setShowProfileModal(true);
      setMessage({
        type: 'info',
        text: 'Silakan lengkapi Data Orang Tua & Keluarga terlebih dahulu (cukup diisi 1 kali untuk pendaftaran seluruh putra/putri Anda).',
      });
      return;
    }
    setSelectedLevel(null);
    setShowRegisterModal(true);
  };

  const handleSaveParentProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setMessage(null);
    setModalFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admissions/parent-profile`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(profileForm),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setParentProfile(data.data.user);
        updateModalFeedback({
          type: 'success',
          text: 'Data Orang Tua & Keluarga berhasil disimpan. Data otomatis terhubung untuk seluruh calon siswa.',
        });
        setMessage({
          type: 'success',
          text: 'Data Orang Tua & Keluarga berhasil disimpan. Data ini otomatis terisi untuk seluruh pendaftaran anak Anda.',
        });
        if (pendingAddChildAfterProfile) {
          setPendingAddChildAfterProfile(false);
          setShowProfileModal(false);
          setSelectedLevel(null);
          setShowRegisterModal(true);
        } else {
          setTimeout(() => setShowProfileModal(false), 1200);
        }
      } else {
        updateModalFeedback({ type: 'error', text: data.message || 'Gagal menyimpan profil orang tua' });
      }
    } catch (err) {
      updateModalFeedback({ type: 'error', text: 'Terjadi kesalahan saat menyimpan profil orang tua' });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSelectLevelCard = (lvl) => {
    if (!lvl.is_open) return;
    setSelectedLevel(lvl);
    setPromoCheck({ loading: false, valid: false, info: null, error: '' });
    setChildForm({
      campus_location: portalConfig.campuses?.[0]?.label || 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13',
      grade_target: lvl.available_grades?.[0] || lvl.level_name,
      intake_type: 'Online Website',
      joint_date: activePeriod?.start_date || '2026-07-15',
      child_full_name: '',
      birth_place: '',
      birth_date: '',
      gender: 'Male',
      nationality: parentProfile?.father_nationality || 'Indonesian (WNI)',
      previous_school: '',
      promo_code: '',
      documents: {
        birth_certificate: '',
        child_photo: '',
        report_card: '',
      },
    });
  };

  const handleValidatePromo = async () => {
    if (!childForm.promo_code.trim()) return;
    setPromoCheck({ loading: true, valid: false, info: null, error: '' });
    try {
      const res = await fetch(`${apiBase}/admissions/validate-promo`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          promo_code: childForm.promo_code.trim(),
          level_code: selectedLevel?.level_code || 'primary',
        }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setPromoCheck({ loading: false, valid: true, info: data.data, error: '' });
      } else {
        setPromoCheck({ loading: false, valid: false, info: null, error: data.message || 'Kode promo tidak valid' });
      }
    } catch (_) {
      setPromoCheck({ loading: false, valid: false, info: null, error: 'Gagal memeriksa kode promo' });
    }
  };

  const handleSubmitChildRegistration = async (e) => {
    e.preventDefault();
    if (!selectedLevel) return;

    setSubmittingChild(true);
    setMessage(null);
    setModalFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admissions/applications`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          level_code: selectedLevel.level_code,
          form_payload: childForm,
        }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        updateModalFeedback({
          type: 'success',
          text: data.message || 'Pendaftaran calon siswa berhasil disimpan!',
        });
        setMessage({
          type: 'success',
          text: data.message || 'Pendaftaran calon siswa berhasil disimpan!',
        });
        setTimeout(() => {
          setShowRegisterModal(false);
          setSelectedLevel(null);
        }, 1200);
        await loadAllData();
      } else {
        updateModalFeedback({ type: 'error', text: data.message || 'Gagal mengirim pendaftaran anak' });
      }
    } catch (err) {
      updateModalFeedback({ type: 'error', text: 'Terjadi kesalahan saat menyimpan pendaftaran' });
    } finally {
      setSubmittingChild(false);
    }
  };

  const handleOpenChildDetail = (child) => {
    setActiveChildDetail(child);
    setRevisionDocs({});
    setModalFeedback(null);
    setUploadedFilesMeta((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith('rev_')) delete next[k];
      });
      return next;
    });
    setUploadProgress((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith('rev_')) delete next[k];
      });
      return next;
    });
    const invTotal = child.invoice?.total_payable || 0;

    // Parse existing payment note format if exists: NamaPengirim_Bank_NamaAnak
    let senderName = parentProfile?.full_name || '';
    let senderBank = 'Bank BCA';
    let addNotes = '';

    if (child.payment_notes && child.payment_notes.includes('_')) {
      const parts = child.payment_notes.split('_');
      if (parts[0]) senderName = parts[0].trim();
      if (parts[1]) senderBank = parts[1].trim();
      if (parts.length > 2) {
        const remaining = parts.slice(2).join('_');
        const match = remaining.split(' - ');
        if (match.length > 1) {
          addNotes = match.slice(1).join(' - ').trim();
        }
      }
    }

    setPaymentForm({
      payment_method: child.payment_method || 'transfer_bank',
      payment_amount_paid: child.payment_amount_paid || invTotal || '',
      payment_proof_url: child.payment_proof_url || '',
      sender_account_name: senderName,
      sender_bank_name: senderBank,
      additional_notes: addNotes,
      payment_notes: child.payment_notes || '',
    });
  };

  const handleResubmitDocuments = async (e) => {
    e.preventDefault();
    if (!activeChildDetail) return;
    setSubmittingRevision(true);
    setModalFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admissions/applications/${activeChildDetail.id}/documents`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ documents: revisionDocs }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        updateModalFeedback({ type: 'success', text: data.message || 'Pembaruan berkas berhasil disimpan.' });
        setMessage({ type: 'success', text: data.message });
        await loadAllData();
      } else {
        updateModalFeedback({ type: 'error', text: data.message || 'Gagal memperbarui berkas' });
      }
    } catch (_) {
      updateModalFeedback({ type: 'error', text: 'Gagal mengirim pembaruan berkas' });
    } finally {
      setSubmittingRevision(false);
    }
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!activeChildDetail) return;
    setSubmittingPayment(true);
    setModalFeedback(null);
    try {
      // Build standardized format: Nama Rekening Pengirim_Bank Pengirim_Nama Anak
      let finalNote = '';
      if (paymentForm.payment_method === 'transfer_bank') {
        const accName = (paymentForm.sender_account_name || parentProfile?.full_name || '').trim();
        const bankName = (paymentForm.sender_bank_name || 'Bank BCA').trim();
        const childName = (activeChildDetail.child_full_name || '').trim();
        finalNote = `${accName}_${bankName}_${childName}`;
        if (paymentForm.additional_notes?.trim()) {
          finalNote += ` - ${paymentForm.additional_notes.trim()}`;
        }
      } else {
        finalNote = `Kasir Sekolah_Pembayaran Langsung_${activeChildDetail.child_full_name}${paymentForm.additional_notes ? ` - ${paymentForm.additional_notes.trim()}` : ''}`;
      }

      const payload = {
        payment_method: paymentForm.payment_method,
        payment_amount_paid: paymentForm.payment_amount_paid,
        payment_proof_url: paymentForm.payment_proof_url,
        payment_notes: finalNote,
      };

      const res = await fetch(`${apiBase}/admissions/applications/${activeChildDetail.id}/payment`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        updateModalFeedback({
          type: 'success',
          text: data.message || 'Bukti pembayaran berhasil dikirim. Menunggu verifikasi dari Admin Admissions.',
        });
        setMessage({ type: 'success', text: data.message });
        await loadAllData();
      } else {
        updateModalFeedback({ type: 'error', text: data.message || 'Gagal mengirim konfirmasi pembayaran' });
      }
    } catch (_) {
      updateModalFeedback({ type: 'error', text: 'Gagal mengirim bukti pembayaran' });
    } finally {
      setSubmittingPayment(false);
    }
  };

  const getStatusMeta = (status) => {
    const map = {
      submitted: { label: 'Menunggu Validasi Berkas', tone: 'blue', step: 1 },
      in_review: { label: 'Sedang Direview Admin', tone: 'blue', step: 2 },
      document_revision: { label: 'Perlu Revisi Berkas', tone: 'orange', step: 2 },
      revision_required: { label: 'Perlu Revisi Berkas', tone: 'orange', step: 2 },
      test_scheduled: { label: 'Jadwal Tes Offline Terbit', tone: 'purple', step: 3 },
      test_passed: { label: 'Lulus Seleksi - Menunggu Pembayaran', tone: 'teal', step: 4 },
      test_failed: { label: 'Belum Lulus Seleksi', tone: 'red', step: 4 },
      rejected: { label: 'Tidak Disetujui', tone: 'red', step: 4 },
      payment_submitted: { label: 'Menunggu Verifikasi Pembayaran', tone: 'yellow', step: 5 },
      payment_revision: { label: 'Perlu Revisi Bukti Bayar', tone: 'orange', step: 5 },
      enrolled: { label: 'Resmi Siswa Permata Harapan', tone: 'green', step: 6 },
      approved: { label: 'Resmi Siswa Permata Harapan', tone: 'green', step: 6 },
    };
    return map[status] || { label: status, tone: 'gray', step: 1 };
  };

  const resolveFileLink = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const baseRoot = apiBase.replace(/\/api\/?$/, '');
    return `${baseRoot}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const activePeriod = portalConfig.active_period;
  const scheduledTestsCount = children.filter((c) => c.status_code === 'test_scheduled').length;
  const pendingPaymentCount = children.filter((c) =>
    ['test_passed', 'payment_submitted', 'payment_revision'].includes(c.status_code)
  ).length;
  const enrolledCount = children.filter((c) => c.status_code === 'enrolled').length;
  const isProfileComplete = Boolean(parentProfile?.is_profile_complete);

  return (
    <DashboardLayout
      user={parentProfile?.full_name ? parentProfile : user}
      userRole="parent"
      onLogout={onLogout}
      activeTab={activeTab}
      onTabChange={(tab) => {
        setActiveTab(tab);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
      totalChildrenCount={children.length}
      scheduledTestsCount={scheduledTestsCount}
      pendingPaymentCount={pendingPaymentCount}
      isProfileComplete={isProfileComplete}
      apiBase={apiBase}
    >
      <div className="pd-portal-wrapper">
        {/* Mode Tablet Walk-in / Kiosk Isolation Banner */}
        {isKioskMode && (
          <div className="pd-kiosk-banner">
            <div className="pd-kiosk-info">
              <ShieldCheck size={24} color="#f59e0b" />
              <div>
                <strong>MODE TABLET WALK-IN / KIOSK AKTIF — Perangkat Bersama Sekolah Permata Harapan</strong>
                <p>Demi keamanan & kerahasiaan data keluarga Anda, pastikan untuk menekan tombol di samping setelah selesai mendaftar agar pendaftar berikutnya tidak dapat mengakses data Anda.</p>
              </div>
            </div>
            <button type="button" className="pd-kiosk-logout-btn" onClick={handleKioskExit}>
              Selesai & Bersihkan Sesi (Logout Aman)
            </button>
          </div>
        )}

        {/* =====================================================================
            TAB 1: DASHBOARD (RINGKASAN PPDB)
        ===================================================================== */}
        {(activeTab === 'dashboard' || activeTab === 'overview') && (
          <div className="pd-tab-panel">
            {/* Welcome Banner */}
            <section className="pd-welcome-banner">
              <div className="pd-welcome-text">
                <span className="pd-welcome-badge">Portal Orang Tua & Siswa Baru</span>
                <h2>Selamat Datang, {parentProfile?.full_name || user?.full_name || 'Bapak/Ibu Calon Wali'}</h2>
                <p>
                  Sistem Penerimaan Siswa Baru Multi-Anak Sekolah Permata Harapan (TA {activePeriod?.academic_year || '2026/2027'}). 
                  Anda dapat mendaftarkan beberapa anak sekaligus, memantau berkas, jadwal tes, surat penerimaan (LoA), dan administrasi pembayaran secara mandiri atau melalui tablet sekolah.
                </p>
              </div>
              <div className="pd-welcome-actions">
                <button type="button" className="pd-btn-primary pd-touch-btn" onClick={handleStartAddChild}>
                  <Plus size={16} />
                  <span>Daftarkan Calon Siswa</span>
                </button>
                <button
                  type="button"
                  className="pd-btn-secondary pd-touch-btn"
                  onClick={() => {
                    setPendingAddChildAfterProfile(false);
                    setShowProfileModal(true);
                  }}
                >
                  <Edit3 size={15} />
                  <span>{isProfileComplete ? 'Perbarui Data Keluarga' : 'Lengkapi Data Keluarga'}</span>
                </button>
              </div>
            </section>

            {/* Incomplete Profile Callout */}
            {!isProfileComplete && (
              <div className="pd-profile-alert-card">
                <div className="pd-profile-alert-icon">
                  <AlertCircle size={24} />
                </div>
                <div className="pd-profile-alert-body">
                  <h4>Data Orang Tua & Keluarga Belum Dilengkapi</h4>
                  <p>
                    Data ayah, ibu, alamat tempat tinggal, serta berkas KTP & Kartu Keluarga cukup Anda lengkapi <strong>satu kali</strong> dan otomatis terhubung untuk seluruh anak yang Anda daftarkan.
                  </p>
                </div>
                <button
                  type="button"
                  className="pd-btn-warning pd-touch-btn"
                  onClick={() => {
                    setPendingAddChildAfterProfile(true);
                    setShowProfileModal(true);
                  }}
                >
                  <UserCheck size={16} />
                  <span>Lengkapi Sekarang</span>
                </button>
              </div>
            )}

            {/* KPI Metric Cards */}
            <div className="pd-kpi-grid">
              <div className="pd-kpi-card" onClick={() => setActiveTab('children')}>
                <div className="pd-kpi-icon blue">
                  <Users size={22} />
                </div>
                <div className="pd-kpi-info">
                  <span className="pd-kpi-label">Total Anak Didaftarkan</span>
                  <strong className="pd-kpi-value">{children.length}</strong>
                  <span className="pd-kpi-sub">
                    {children.length > 1 ? 'Multi-Anak Terdaftar' : 'Satu Akun Multi-Anak'}
                  </span>
                </div>
              </div>

              <div className="pd-kpi-card" onClick={() => setActiveTab('test-schedule')}>
                <div className="pd-kpi-icon purple">
                  <CalendarClock size={22} />
                </div>
                <div className="pd-kpi-info">
                  <span className="pd-kpi-label">Jadwal Tes Offline</span>
                  <strong className="pd-kpi-value">{scheduledTestsCount}</strong>
                  <span className="pd-kpi-sub">Observasi & Asesmen Kampus</span>
                </div>
              </div>

              <div className="pd-kpi-card" onClick={() => setActiveTab('payments')}>
                <div className="pd-kpi-icon orange">
                  <CreditCard size={22} />
                </div>
                <div className="pd-kpi-info">
                  <span className="pd-kpi-label">Menunggu Pembayaran</span>
                  <strong className="pd-kpi-value">{pendingPaymentCount}</strong>
                  <span className="pd-kpi-sub">LoA Terbit / Verifikasi Bukti</span>
                </div>
              </div>

              <div className="pd-kpi-card" onClick={() => setActiveTab('children')}>
                <div className="pd-kpi-icon green">
                  <ShieldCheck size={22} />
                </div>
                <div className="pd-kpi-info">
                  <span className="pd-kpi-label">Resmi Siswa PHS</span>
                  <strong className="pd-kpi-value">{enrolledCount}</strong>
                  <span className="pd-kpi-sub">Nomor Induk Siswa (NIS) Terbit</span>
                </div>
              </div>
            </div>

            {/* Hero Grid: Period Quota & Family Profile Card */}
            <div className="pd-hero-grid">
              <div className="pd-period-card">
                <div className="pd-period-header">
                  <span className="pd-pill-tag">Periode Pendaftaran Aktif</span>
                  {activePeriod && (
                    <span className="pd-academic-year">Tahun Ajaran {activePeriod.academic_year}</span>
                  )}
                </div>
                <h2>{activePeriod ? activePeriod.period_name : 'Penerimaan Siswa Baru TA 2026/2027'}</h2>
                <p className="pd-period-dates">
                  <Calendar size={15} />
                  <span>
                    Periode Pendaftaran:{' '}
                    {activePeriod
                      ? `${formatDateId(activePeriod.start_date)} s/d ${formatDateId(activePeriod.end_date)}`
                      : 'Terbuka untuk Pendaftaran Online & Offline'}
                  </span>
                </p>
                <p className="pd-period-note">
                  Apabila kuota jenjang atau batas tanggal pada periode ini telah penuh/berakhir, pendaftaran anak Anda secara otomatis akan dialokasikan ke Periode Pendaftaran Selanjutnya.
                </p>

                {activePeriod?.quotas && (
                  <div className="pd-quota-strip">
                    {portalConfig.levels
                      .filter((l) => l.is_open && l.level_code !== 'sma')
                      .map((lvl) => {
                        const limit = Number(activePeriod.quotas?.[lvl.level_code] ?? 30);
                        const used = Number(activePeriod.filled?.[lvl.level_code] ?? 0);
                        const remaining = Math.max(0, limit - used);
                        return (
                          <div key={lvl.level_code} className="pd-quota-chip">
                            <span className="pd-quota-lvl">{lvl.tag_code}</span>
                            <span className={`pd-quota-val ${remaining === 0 ? 'full' : ''}`}>
                              {remaining > 0 ? `Sisa ${remaining} Kursi` : 'Kuota Penuh (Auto Gelombang Selanjutnya)'}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>

              <div className="pd-family-card">
                <div className="pd-family-top">
                  <div>
                    <span className="pd-family-kicker">Profil Keluarga (1 Akun Multi-Anak)</span>
                    <h3>{parentProfile?.full_name || 'Bapak/Ibu Orang Tua'}</h3>
                  </div>
                  <button
                    type="button"
                    className="pd-btn-outline-sm pd-touch-btn"
                    onClick={() => {
                      setPendingAddChildAfterProfile(false);
                      setShowProfileModal(true);
                    }}
                  >
                    <Edit3 size={14} />
                    <span>{isProfileComplete ? 'Edit Data' : 'Lengkapi Data'}</span>
                  </button>
                </div>

                <div className="pd-family-meta">
                  <div>
                    <span>Ayah / Ibu:</span>
                    <strong>
                      {parentProfile?.father_name || '-'} / {parentProfile?.mother_name || '-'}
                    </strong>
                  </div>
                  <div>
                    <span>Alamat Rumah:</span>
                    <strong>{parentProfile?.home_address || 'Belum dilengkapi'}</strong>
                  </div>
                  <div>
                    <span>Berkas Keluarga:</span>
                    <strong>
                      {parentProfile?.parent_ktp_url && parentProfile?.family_card_url
                        ? 'KTP & KK Tersimpan'
                        : 'KTP / KK Belum Lengkap'}
                    </strong>
                  </div>
                </div>

                <div className="pd-family-stats">
                  <div className="pd-mini-stat" onClick={() => setActiveTab('children')}>
                    <strong>{children.length}</strong>
                    <span>Anak Terdaftar</span>
                  </div>
                  <div className="pd-mini-stat" onClick={() => setActiveTab('test-schedule')}>
                    <strong>{scheduledTestsCount}</strong>
                    <span>Jadwal Tes</span>
                  </div>
                  <div className="pd-mini-stat" onClick={() => setActiveTab('payments')}>
                    <strong>{enrolledCount}</strong>
                    <span>Resmi Siswa</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Children Section */}
            <section className="pd-children-section">
              <div className="pd-section-head">
                <div>
                  <h2>Calon Siswa yang Sedang Berproses</h2>
                  <p>
                    Pantau tahapan pendaftaran masing-masing anak mulai dari verifikasi berkas, tes offline, LoA, hingga pembayaran.
                  </p>
                </div>
                <div className="pd-section-actions">
                  <button type="button" className="pd-btn-secondary pd-touch-btn" onClick={loadAllData}>
                    <RefreshCw size={15} />
                    <span>Refresh</span>
                  </button>
                  <button type="button" className="pd-btn-primary pd-touch-btn" onClick={handleStartAddChild}>
                    <Plus size={16} />
                    <span>Daftarkan Anak Baru</span>
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="pd-empty-box">Memuat data pendaftaran calon siswa...</div>
              ) : children.length === 0 ? (
                <div className="pd-empty-box">
                  <Users size={42} className="pd-empty-icon" />
                  <h3>Belum Ada Calon Siswa yang Didaftarkan</h3>
                  <p>
                    Silakan mulai mendaftarkan putra/putri Anda ke jenjang Preschool, Kindergarten, Primary, atau Secondary.
                  </p>
                  <button type="button" className="pd-btn-primary pd-touch-btn" onClick={handleStartAddChild}>
                    <Plus size={16} />
                    <span>Daftarkan Calon Siswa Pertama</span>
                  </button>
                </div>
              ) : (
                <div className="pd-children-grid">
                  {children.map((child) => renderChildCard(child))}
                </div>
              )}
            </section>
          </div>
        )}

        {/* =====================================================================
            TAB 2: DAFTAR ANAK (CHILDREN)
        ===================================================================== */}
        {activeTab === 'children' && (
          <div className="pd-tab-panel">
            <div className="pd-panel-header">
              <div>
                <h2>Pendaftaran Calon Siswa (Multi-Anak)</h2>
                <p>
                  Kelola pendaftaran seluruh putra/putri Anda. Anda dapat mendaftarkan lebih dari 1 anak pada jenjang yang sama maupun berbeda secara terpadu dalam satu akun keluarga.
                </p>
              </div>
              <div className="pd-panel-actions">
                <button type="button" className="pd-btn-secondary pd-touch-btn" onClick={loadAllData}>
                  <RefreshCw size={15} />
                  <span>Refresh Data</span>
                </button>
                <button type="button" className="pd-btn-primary pd-touch-btn" onClick={handleStartAddChild}>
                  <Plus size={16} />
                  <span>+ Daftarkan Anak Baru</span>
                </button>
              </div>
            </div>

            {loading ? (
              <div className="pd-empty-box">Memuat data pendaftaran anak...</div>
            ) : children.length === 0 ? (
              <div className="pd-empty-box">
                <Users size={42} className="pd-empty-icon" />
                <h3>Belum Ada Anak yang Didaftarkan</h3>
                <p>Mulai daftarkan putra/putri Anda sekarang.</p>
                <button type="button" className="pd-btn-primary pd-touch-btn" onClick={handleStartAddChild}>
                  <Plus size={16} />
                  <span>Daftarkan Anak Pertama</span>
                </button>
              </div>
            ) : (
              <div className="pd-children-grid">
                {children.map((child) => renderChildCard(child))}
              </div>
            )}
          </div>
        )}

        {/* =====================================================================
            TAB 3: PROFIL KELUARGA (FAMILY PROFILE)
        ===================================================================== */}
        {activeTab === 'family-profile' && (
          <div className="pd-tab-panel">
            <div className="pd-panel-header">
              <div>
                <h2>Profil Orang Tua, Alamat & Berkas Keluarga</h2>
                <p>
                  Sistem 1 Akun Multi-Anak: Data orang tua, domisili, dan berkas KTP & Kartu Keluarga di bawah ini cukup diisi satu kali dan otomatis digunakan untuk semua pendaftaran anak.
                </p>
              </div>
              <div className="pd-panel-actions">
                <button
                  type="button"
                  className="pd-btn-primary pd-touch-btn"
                  onClick={() => {
                    setPendingAddChildAfterProfile(false);
                    setShowProfileModal(true);
                  }}
                >
                  <Edit3 size={15} />
                  <span>Edit Profil Keluarga</span>
                </button>
              </div>
            </div>

            <div className="pd-profile-view-grid">
              {/* Card 1: Ayah & Ibu */}
              <div className="pd-profile-block">
                <div className="pd-profile-block-head">
                  <Users size={18} />
                  <h3>1. Data Ayah & Ibu / Wali</h3>
                </div>
                <div className="pd-profile-info-grid">
                  <div>
                    <span>Nama Ayah</span>
                    <strong>{parentProfile?.father_name || '-'}</strong>
                  </div>
                  <div>
                    <span>No. WhatsApp Ayah</span>
                    <strong>{parentProfile?.father_phone || '-'}</strong>
                  </div>
                  <div>
                    <span>Pekerjaan Ayah</span>
                    <strong>{parentProfile?.father_occupation || '-'}</strong>
                  </div>
                  <div>
                    <span>Kewarganegaraan Ayah</span>
                    <strong>{parentProfile?.father_nationality || '-'}</strong>
                  </div>
                  <div className="pd-divider-row" />
                  <div>
                    <span>Nama Ibu</span>
                    <strong>{parentProfile?.mother_name || '-'}</strong>
                  </div>
                  <div>
                    <span>No. WhatsApp Ibu</span>
                    <strong>{parentProfile?.mother_phone || '-'}</strong>
                  </div>
                  <div>
                    <span>Pekerjaan Ibu</span>
                    <strong>{parentProfile?.mother_occupation || '-'}</strong>
                  </div>
                  <div>
                    <span>Kewarganegaraan Ibu</span>
                    <strong>{parentProfile?.mother_nationality || '-'}</strong>
                  </div>
                </div>
              </div>

              {/* Card 2: Alamat Domisili */}
              <div className="pd-profile-block">
                <div className="pd-profile-block-head">
                  <MapPin size={18} />
                  <h3>2. Alamat Domisili Tempat Tinggal Keluarga</h3>
                </div>
                <div className="pd-profile-info-grid">
                  <div style={{ gridColumn: 'span 2' }}>
                    <span>Alamat Lengkap Rumah</span>
                    <strong>{parentProfile?.home_address || 'Belum diisi'}</strong>
                  </div>
                  <div>
                    <span>Kota / Kabupaten</span>
                    <strong>{parentProfile?.city || 'Jakarta Barat'}</strong>
                  </div>
                  <div>
                    <span>Kode Pos</span>
                    <strong>{parentProfile?.postal_code || '-'}</strong>
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <span>No. Telepon Rumah / Utama</span>
                    <strong>{parentProfile?.phone || '-'}</strong>
                  </div>
                </div>
              </div>

              {/* Card 3: Berkas Keluarga */}
              <div className="pd-profile-block" style={{ gridColumn: 'span 2' }}>
                <div className="pd-profile-block-head">
                  <FileText size={18} />
                  <h3>3. Berkas Dokumen Identitas Keluarga (KTP & KK)</h3>
                </div>
                <div className="pd-docs-view-row">
                  <div className="pd-doc-view-card">
                    <div>
                      <strong>KTP Orang Tua / Paspor KITAS</strong>
                      <span className={parentProfile?.parent_ktp_url ? 'status-ok' : 'status-missing'}>
                        {parentProfile?.parent_ktp_url ? 'Dokumen Tersimpan' : 'Belum Diunggah'}
                      </span>
                    </div>
                    {parentProfile?.parent_ktp_url ? (
                      <a
                        href={resolveFileLink(parentProfile.parent_ktp_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="pd-btn-outline-sm pd-touch-btn"
                      >
                        <ExternalLink size={14} />
                        <span>Lihat KTP</span>
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="pd-btn-outline-sm pd-touch-btn"
                        onClick={() => setShowProfileModal(true)}
                      >
                        <Upload size={14} />
                        <span>Upload KTP</span>
                      </button>
                    )}
                  </div>

                  <div className="pd-doc-view-card">
                    <div>
                      <strong>Kartu Keluarga (KK)</strong>
                      <span className={parentProfile?.family_card_url ? 'status-ok' : 'status-missing'}>
                        {parentProfile?.family_card_url ? 'Dokumen Tersimpan' : 'Belum Diunggah'}
                      </span>
                    </div>
                    {parentProfile?.family_card_url ? (
                      <a
                        href={resolveFileLink(parentProfile.family_card_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="pd-btn-outline-sm pd-touch-btn"
                      >
                        <ExternalLink size={14} />
                        <span>Lihat KK</span>
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="pd-btn-outline-sm pd-touch-btn"
                        onClick={() => setShowProfileModal(true)}
                      >
                        <Upload size={14} />
                        <span>Upload KK</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* =====================================================================
            TAB 4: JADWAL TES & LoA (TEST SCHEDULE & ACCEPTANCE)
        ===================================================================== */}
        {activeTab === 'test-schedule' && (
          <div className="pd-tab-panel">
            <div className="pd-panel-header">
              <div>
                <h2>Jadwal Tes Offline, Materi Seleksi & LoA</h2>
                <p>
                  Pantau tanggal, waktu, ruangan observasi/asesmen akademik offline untuk masing-masing jenjang (Preschool, Kindergarten, Primary, Secondary), serta surat keputusan penerimaan (Letter of Acceptance).
                </p>
              </div>
              <div className="pd-panel-actions">
                <button type="button" className="pd-btn-secondary pd-touch-btn" onClick={loadAllData}>
                  <RefreshCw size={15} />
                  <span>Refresh Jadwal</span>
                </button>
              </div>
            </div>

            {children.length === 0 ? (
              <div className="pd-empty-box">
                <CalendarClock size={42} className="pd-empty-icon" />
                <h3>Belum Ada Data Calon Siswa</h3>
                <p>Silakan daftarkan calon siswa terlebih dahulu untuk mendapatkan jadwal tes.</p>
                <button type="button" className="pd-btn-primary pd-touch-btn" onClick={handleStartAddChild}>
                  <Plus size={16} />
                  <span>Daftarkan Calon Siswa</span>
                </button>
              </div>
            ) : (
              <div className="pd-schedule-stack">
                {children.map((child) => (
                  <div key={child.id} className="pd-schedule-card">
                    <div className="pd-schedule-card-top">
                      <div>
                        <span className="pd-reg-no">{child.registration_no}</span>
                        <h3>{child.child_full_name}</h3>
                        <p className="pd-child-sub">
                          <School size={14} />
                          <span>{child.level_name} — Kelas {child.grade_target}</span>
                          <span className="pd-dot">|</span>
                          <span>Kampus: {child.campus_location}</span>
                        </p>
                      </div>
                      <div className="pd-child-status-col">
                        <span className={`pd-status-badge tone-${getStatusMeta(child.status_code).tone}`}>
                          {getStatusMeta(child.status_code).label}
                        </span>
                      </div>
                    </div>

                    {/* Test Schedule Card */}
                    {child.test_schedule ? (
                      <div className="pd-test-ticket">
                        <div className="pd-ticket-header">
                          <div>
                            <span className="pd-ticket-tag">KARTU PESERTA TES & OBSERVASI OFFLINE</span>
                            <h4>{child.test_schedule.session_name}</h4>
                          </div>
                          <button
                            type="button"
                            className="pd-btn-test-card pd-touch-btn"
                            onClick={() => setTestCardModalChild(child)}
                          >
                            <Printer size={15} />
                            <span>Cetak Kartu Tes</span>
                          </button>
                        </div>

                        <div className="pd-test-ticket-grid">
                          <div className="pd-ticket-item">
                            <span>Hari & Tanggal</span>
                            <strong>{formatDateId(child.test_schedule.test_date)}</strong>
                          </div>
                          <div className="pd-ticket-item">
                            <span>Waktu Pelaksanaan</span>
                            <strong>{child.test_schedule.test_time}</strong>
                          </div>
                          <div className="pd-ticket-item">
                            <span>Lokasi Kampus & Ruangan</span>
                            <strong>
                              {child.test_schedule.campus_location} ({child.test_schedule.room_name})
                            </strong>
                          </div>
                          <div className="pd-ticket-item">
                            <span>Jenjang & Asesmen</span>
                            <strong>{child.test_schedule.assessment_title}</strong>
                          </div>
                        </div>

                        <div className="pd-test-material-box">
                          <h5>Materi Seleksi & Kesiapan ({child.test_schedule.assessment_title}):</h5>
                          <p>
                            <strong>Fokus Observasi:</strong> {child.test_schedule.assessment_subjects}
                          </p>
                          <p>{child.test_schedule.assessment_description}</p>
                          <p className="pd-material-prep">
                            <strong>Perlengkapan yang Dibawa:</strong> {child.test_schedule.assessment_preparation}
                          </p>
                        </div>
                      </div>
                    ) : child.status_code === 'submitted' || child.status_code === 'in_review' ? (
                      <div className="pd-waiting-schedule-box">
                        <Clock size={20} />
                        <div>
                          <strong>Sedang Dalam Tahap Validasi Berkas oleh Admin Admissions</strong>
                          <p>
                            Jadwal tanggal, sesi, dan ruangan tes offline akan diterbitkan di sini segera setelah dokumen pendaftaran anak Anda diverifikasi lengkap.
                          </p>
                          {child.assessment_info && (
                            <p className="pd-preview-note">
                              Gambaran Materi Tes {child.level_name}: <em>{child.assessment_info.title} — {child.assessment_info.subjects}</em>
                            </p>
                          )}
                        </div>
                      </div>
                    ) : null}

                    {/* Hasil Asesmen Tes Offline & Dokumen Scan PDF */}
                    {(child.test_score !== null || child.test_result_doc_url || child.test_evaluation_notes) && (
                      <div className="pd-test-score-box">
                        <div className="pd-score-box-header">
                          <div className="pd-score-title-wrap">
                            <Award size={20} className="pd-score-icon" />
                            <div>
                              <h4>Hasil Evaluasi & Skor Tes Seleksi ({child.test_schedule?.assessment_title || child.level_name})</h4>
                              <span className="pd-score-subtitle">
                                Hasil penilaian resmi asesmen akademik & observasi dari tim penguji Sekolah Permata Harapan
                              </span>
                            </div>
                          </div>
                          {child.test_score !== null && (
                            <div className="pd-score-badge-circle">
                              <span>Skor Tes</span>
                              <strong>{child.test_score}</strong>
                            </div>
                          )}
                        </div>

                        {child.test_evaluation_notes && (
                          <div className="pd-score-note-row">
                            <strong>Catatan Evaluasi Asesor:</strong>
                            <p>&ldquo;{child.test_evaluation_notes}&rdquo;</p>
                          </div>
                        )}

                        {child.test_result_doc_url && (
                          <div className="pd-score-doc-row">
                            <a
                              href={resolveFileLink(child.test_result_doc_url)}
                              target="_blank"
                              rel="noreferrer"
                              className="pd-btn-download-test-doc pd-touch-btn"
                            >
                              <FileText size={16} />
                              <span>Buka / Unduh Dokumen Hasil Scan Tes (.pdf)</span>
                              <ExternalLink size={13} />
                            </a>
                            <span className="pd-score-doc-hint">
                              Dokumen scan resmi lembar evaluasi tes yang telah dinilai dan diverifikasi.
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* LoA if passed */}
                    {child.status_code === 'test_passed' && (
                      <div className="pd-loa-card" style={{ marginTop: 14 }}>
                        <div className="pd-loa-top">
                          <div>
                            <span className="pd-loa-badge">LETTER OF ACCEPTANCE (LoA)</span>
                            <h4>Surat Penerimaan Siswa Baru: {child.loa_number}</h4>
                            <p>
                              Selamat! Berdasarkan hasil observasi & evaluasi seleksi{child.test_score !== null ? ` (Nilai: ${child.test_score})` : ''},{' '}
                              <strong>{child.child_full_name}</strong> dinyatakan <strong>LULUS SELEKSI</strong> pada jenjang {child.level_name} ({child.grade_target}).
                            </p>
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className="pd-btn-loa-primary pd-touch-btn"
                              onClick={() => setLoaModalChild(child)}
                            >
                              <Award size={15} />
                              <span>Letter of Acceptance (LoA)</span>
                            </button>
                            <button
                              type="button"
                              className="pd-btn-primary pd-touch-btn"
                              onClick={() => {
                                setActiveTab('payments');
                                handleOpenChildDetail(child);
                              }}
                            >
                              <span>Lihat Rincian Biaya & Pembayaran</span>
                              <ChevronRight size={15} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Belum Lulus notice */}
                    {child.status_code === 'test_failed' && (
                      <div className="pd-result-failed-box" style={{ marginTop: 14 }}>
                        <h4>Hasil Evaluasi Seleksi: Belum Memenuhi Standar Kelulusan</h4>
                        <p>{child.test_evaluation_notes || child.admin_note || 'Terima kasih telah mengikuti tahapan seleksi Sekolah Permata Harapan.'}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* =====================================================================
            TAB 5: PEMBAYARAN & NIS (PAYMENTS & ENROLLMENT)
        ===================================================================== */}
        {activeTab === 'payments' && (
          <div className="pd-tab-panel">
            <div className="pd-panel-header">
              <div>
                <h2>Pembayaran Biaya Masuk & Nomor Induk Siswa (NIS)</h2>
                <p>
                  Rincian invoice resmi (Development Fee, SPP Bulan Pertama, Registration Fee), pilihan transfer bank atau pembayaran langsung di kasir sekolah, unggah bukti bayar, dan penetapan NIS resmi siswa.
                </p>
              </div>
              <div className="pd-panel-actions">
                <button type="button" className="pd-btn-secondary pd-touch-btn" onClick={loadAllData}>
                  <RefreshCw size={15} />
                  <span>Refresh Pembayaran</span>
                </button>
              </div>
            </div>

            {children.filter((c) => ['test_passed', 'payment_submitted', 'payment_revision', 'enrolled', 'approved'].includes(c.status_code)).length === 0 ? (
              <div className="pd-empty-box">
                <CreditCard size={42} className="pd-empty-icon" />
                <h3>Belum Ada Tagihan Pembayaran Aktif</h3>
                <p>
                  Sesuai kebijakan penerimaan sekolah, rincian biaya resmi dan kode pembayaran diterbitkan <strong>setelah calon siswa dinyatakan LULUS tes seleksi/observasi offline</strong> dan memperoleh Letter of Acceptance (LoA).
                </p>
              </div>
            ) : (
              <div className="pd-schedule-stack">
                {children
                  .filter((c) => ['test_passed', 'payment_submitted', 'payment_revision', 'enrolled', 'approved'].includes(c.status_code))
                  .map((child) => (
                    <div key={child.id} className="pd-payment-card-wrap">
                      {/* Official Enrolled Student Certificate */}
                      {child.status_code === 'enrolled' && (
                        <div className="pd-official-enrolled-card">
                          <div className="pd-enrolled-header">
                            <div>
                              <span className="pd-enrolled-tag">OFFICIAL STUDENT ENROLLMENT CERTIFICATE</span>
                              <h3>RESMI TERDAFTAR SEBAGAI SISWA SEKOLAH PERMATA HARAPAN</h3>
                              <p>
                                Selamat! Seluruh tahapan seleksi akademik dan administrasi pembayaran untuk{' '}
                                <strong>{child.child_full_name}</strong> telah selesai dan terverifikasi oleh Kepala Sekolah & Admissions.
                              </p>
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                className="pd-btn-loa-primary pd-touch-btn"
                                onClick={() => setEnrollmentCertChild(child)}
                              >
                                <CheckCircle2 size={16} />
                                <span>Bukti Siswa Resmi (Enrollment Certificate)</span>
                              </button>
                              <button
                                type="button"
                                className="pd-btn-primary pd-touch-btn"
                                onClick={() => setReceiptModalChild(child)}
                              >
                                <CreditCard size={16} />
                                <span>Kwitansi Pelunasan (Official Receipt)</span>
                              </button>
                            </div>
                          </div>
                          <div className="pd-enrolled-grid">
                            <div>
                              <span>Nomor Induk Siswa (NIS)</span>
                              <strong>{child.student_id_number}</strong>
                            </div>
                            <div>
                              <span>Nomor Surat Penerimaan (LoA)</span>
                              <strong>{child.loa_number || '-'}</strong>
                            </div>
                            <div>
                              <span>Jenjang & Kelas</span>
                              <strong>{child.level_name} ({child.grade_target})</strong>
                            </div>
                            <div>
                              <span>Lokasi Kampus</span>
                              <strong>{child.campus_location}</strong>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Invoice Table & Payment Box */}
                      {child.invoice && (
                        <div className="pd-invoice-card" style={{ marginTop: 14 }}>
                          <div className="pd-invoice-head">
                            <div>
                              <span className="pd-reg-no">{child.registration_no}</span>
                              <h4>Tagihan Resmi: {child.child_full_name} ({child.level_name})</h4>
                              <p>Nomor Invoice: {child.invoice.invoice_no}</p>
                            </div>
                            <div className="pd-payment-code-box">
                              <span>Kode Pembayaran Unik</span>
                              <strong>{child.invoice.payment_code}</strong>
                            </div>
                          </div>

                          <table className="pd-invoice-table">
                            <thead>
                              <tr>
                                <th>Komponen Biaya (Sesuai Formulir Pendaftaran)</th>
                                <th>Keterangan / Opsi Termin</th>
                                <th className="text-right">Nominal</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Number(child.invoice.registration_fee) > 0 && (
                                <tr>
                                  <td>Registration Fee</td>
                                  <td>Biaya Formulir Awal</td>
                                  <td className="text-right">{formatCurrency(child.invoice.registration_fee)}</td>
                                </tr>
                              )}
                              {Number(child.invoice.test_reg_form_fee) > 0 && (
                                <tr>
                                  <td>Test / Reg. Form Fee</td>
                                  <td>Biaya Asesmen Seleksi</td>
                                  <td className="text-right">{formatCurrency(child.invoice.test_reg_form_fee)}</td>
                                </tr>
                              )}
                              {Number(child.invoice.admission_fee) > 0 && (
                                <tr>
                                  <td>Admission Fee</td>
                                  <td>Biaya Masuk Siswa Baru</td>
                                  <td className="text-right">{formatCurrency(child.invoice.admission_fee)}</td>
                                </tr>
                              )}
                              <tr>
                                <td>
                                  <strong>Development Fee (Uang Gedung)</strong>
                                  {Number(child.invoice.df_1_amount) > 0 && (
                                    <div className="pd-df-installments">
                                      Termin: DF 1: {formatCurrency(child.invoice.df_1_amount)} | DF 2: {formatCurrency(child.invoice.df_2_amount)} | DF 3: {formatCurrency(child.invoice.df_3_amount)}{Number(child.invoice.df_4_amount) > 0 ? ` | DF 4: ${formatCurrency(child.invoice.df_4_amount)}` : ''}
                                    </div>
                                  )}
                                </td>
                                <td>{child.invoice.df_due_notes || 'Jatuh tempo saat daftar ulang'}</td>
                                <td className="text-right">{formatCurrency(child.invoice.development_fee)}</td>
                              </tr>
                              {Number(child.invoice.books_activity_fee) > 0 && (
                                <tr>
                                  <td>Books & Activity Fee</td>
                                  <td>{child.invoice.books_due_notes || 'Buku & Seragam'}</td>
                                  <td className="text-right">{formatCurrency(child.invoice.books_activity_fee)}</td>
                                </tr>
                              )}
                              <tr>
                                <td>Tuition Fee (SPP Bulan Pertama)</td>
                                <td>SPP Bulanan</td>
                                <td className="text-right">{formatCurrency(child.invoice.tuition_fee)}</td>
                              </tr>
                              {Number(child.invoice.discount_amount) > 0 && (
                                <tr className="pd-discount-row">
                                  <td>Potongan Diskon ({child.invoice.discount_label || 'Promo Voucher'})</td>
                                  <td>Potongan Resmi</td>
                                  <td className="text-right">- {formatCurrency(child.invoice.discount_amount)}</td>
                                </tr>
                              )}
                            </tbody>
                            <tfoot>
                              <tr>
                                <th colSpan={2}>Total Biaya Pembayaran</th>
                                <th className="text-right pd-total-amount">
                                  {formatCurrency(child.invoice.total_payable)}
                                </th>
                              </tr>
                            </tfoot>
                          </table>

                          <div className="pd-bank-info-bar">
                            <div>
                              <span>Rekening Transfer Resmi Sekolah:</span>
                              <strong>
                                {child.invoice.bank_name} — No. Rek: {child.invoice.bank_account_number} a/n {child.invoice.bank_account_holder}
                              </strong>
                            </div>
                            <span className="pd-non-refundable">{child.invoice.disclaimer}</span>
                          </div>

                          {/* Status Actions & User-Friendly Revision / Waiting Alerts */}
                          {child.status_code === 'payment_revision' && (
                            <div className="pd-payment-revision-box">
                              <div className="pd-revision-header">
                                <div className="pd-revision-title-wrap">
                                  <AlertCircle size={22} className="pd-revision-icon" />
                                  <div>
                                    <h4>Bukti Pembayaran Perlu Diperbaiki / Diunggah Ulang</h4>
                                    <span className="pd-revision-subtext">
                                      Admin Admissions telah meninjau bukti pembayaran Anda dan memerlukan perbaikan.
                                    </span>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="pd-btn-warning pd-touch-btn"
                                  onClick={() => handleOpenChildDetail(child)}
                                >
                                  <Upload size={15} />
                                  <span>Unggah Ulang Bukti Bayar</span>
                                </button>
                              </div>

                              <div className="pd-revision-admin-note-box">
                                <strong>Catatan Alasan Revisi dari Admin Admissions:</strong>
                                <p>&ldquo;{child.admin_note || 'Bukti transfer buram, terpotong, atau nominal belum sesuai. Silakan periksa kembali dan unggah struk yang jelas.'}&rdquo;</p>
                              </div>
                            </div>
                          )}

                          {child.status_code === 'payment_submitted' && (
                            <div className="pd-payment-waiting-box">
                              <Clock size={20} className="pd-waiting-icon" />
                              <div>
                                <strong>Bukti Pembayaran Berhasil Dikirim — Sedang Diverifikasi Admin</strong>
                                <p>
                                  Tim Admissions sedang memvalidasi dana dan bukti transfer Anda. Begitu pembayaran diverifikasi dan disetujui, status calon siswa otomatis resmi menjadi Siswa dan NIS akan diterbitkan.
                                </p>
                              </div>
                            </div>
                          )}

                          {child.status_code === 'test_passed' && (
                            <div className="pd-invoice-footer-action">
                              {child.level_fee_approval_status && child.level_fee_approval_status !== 'approved' ? (
                                <div className="pd-pending-fee-approval-notice">
                                  <Clock size={18} color="#d97706" />
                                  <div>
                                    <strong>Menunggu Otorisasi Tarif Resmi oleh Direktur Sekolah (School Director)</strong>
                                    <p>
                                      Rincian tarif biaya untuk jenjang ini sedang dalam proses review dan persetujuan Direktur Sekolah. Tombol konfirmasi pembayaran akan dibuka secara otomatis begitu tarif resmi disahkan.
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <span className="pd-invoice-status-note">
                                    Status: <strong>Lulus Seleksi — Menunggu Penyelesaian Pembayaran</strong>
                                  </span>
                                  <button
                                    type="button"
                                    className="pd-btn-primary pd-touch-btn"
                                    onClick={() => handleOpenChildDetail(child)}
                                  >
                                    <Upload size={15} />
                                    <span>Pilih Cara Bayar & Unggah Bukti</span>
                                  </button>
                                </>
                              )}
                            </div>
                          )}

                          {child.status_code === 'enrolled' && (
                            <div className="pd-invoice-footer-action enrolled">
                              <span className="pd-invoice-status-note">
                                Status: <strong style={{ color: '#059669' }}>LUNAS & Resmi Terdaftar Siswa (NIS: {child.student_id_number})</strong>
                              </span>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {child.official_receipt_doc_url && (
                                  <a
                                    href={resolveFileLink(child.official_receipt_doc_url)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="pd-btn-secondary pd-touch-btn"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    <Download size={15} />
                                    <span>Unduh Kwitansi Fisik (Cap Sekolah)</span>
                                  </a>
                                )}
                                <button
                                  type="button"
                                  className="pd-btn-print-action pd-touch-btn"
                                  onClick={() => setReceiptModalChild(child)}
                                >
                                  <Printer size={15} />
                                  <span>Cetak Bukti Siswa &amp; Kwitansi Resmi</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* =====================================================================
            TAB 6: PANDUAN & BANTUAN (GUIDE & SUPPORT)
        ===================================================================== */}
        {activeTab === 'guide' && (
          <div className="pd-tab-panel">
            <div className="pd-panel-header">
              <div>
                <h2>Panduan Alur Pendaftaran & Layanan Bantuan PPDB</h2>
                <p>
                  Informasi alur pendaftaran siswa baru, dokumen yang diperlukan, dan layanan pendampingan admissions di kampus Sekolah Permata Harapan.
                </p>
              </div>
            </div>

            {/* Workflow steps */}
            <div className="pd-workflow-grid">
              <div className="pd-workflow-card">
                <span className="pd-step-no">01</span>
                <h4>Akun Orang Tua & Profil Keluarga</h4>
                <p>
                  Cukup 1 akun untuk mendaftarkan seluruh anak. Isi data ayah, ibu, domisili, dan berkas KTP & KK 1 kali untuk semua calon siswa.
                </p>
              </div>

              <div className="pd-workflow-card">
                <span className="pd-step-no">02</span>
                <h4>Pilih Jenjang & Berkas Anak</h4>
                <p>
                  Pilih Preschool, Kindergarten, Primary, atau Secondary. Unggah akta kelahiran, pas foto, dan rapor sekolah sebelumnya.
                </p>
              </div>

              <div className="pd-workflow-card">
                <span className="pd-step-no">03</span>
                <h4>Validasi Berkas & Terbit Jadwal Tes</h4>
                <p>
                  Tim Admissions memverifikasi kelengkapan berkas. Begitu valid, jadwal tanggal, sesi jam, dan ruangan tes offline langsung terbit.
                </p>
              </div>

              <div className="pd-workflow-card">
                <span className="pd-step-no">04</span>
                <h4>Observasi & Asesmen Offline di Sekolah</h4>
                <p>
                  Orang tua dan calon siswa hadir ke kampus sesuai jadwal untuk mengikuti observasi psikomotorik, tes akademik, dan wawancara.
                </p>
              </div>

              <div className="pd-workflow-card">
                <span className="pd-step-no">05</span>
                <h4>Letter of Acceptance (LoA) & Pembayaran</h4>
                <p>
                  Setelah lulus asesmen, sekolah menerbitkan LoA resmi dan invoice rincian biaya. Bayar via transfer/kode bayar atau kasir sekolah.
                </p>
              </div>

              <div className="pd-workflow-card">
                <span className="pd-step-no">06</span>
                <h4>Resmi Menjadi Siswa & Nomor Induk (NIS)</h4>
                <p>
                  Verifikasi pembayaran selesai! Anak Anda resmi dinyatakan sebagai siswa Sekolah Permata Harapan dan diterbitkan NIS resmi.
                </p>
              </div>
            </div>

            {/* Contact & Campus Desk */}
            <div className="pd-support-grid">
              <div className="pd-support-card">
                <Building2 size={24} className="pd-support-icon" />
                <h3>Lokasi Kampus Sekolah Permata Harapan</h3>
                <div className="pd-campus-address-list">
                  <div>
                    <strong>Campus 1 (Pos Pengumben)</strong>
                    <p>Jl. Pos Pengumben Lama No.13, Srengseng, Kec. Kembangan, Kota Jakarta Barat</p>
                  </div>
                  <div>
                    <strong>Campus 2 (Srengseng)</strong>
                    <p>Jl. Srengseng Raya No.69, Srengseng, Kec. Kembangan, Kota Jakarta Barat</p>
                  </div>
                </div>
              </div>

              <div className="pd-support-card">
                <HelpCircle size={24} className="pd-support-icon" />
                <h3>Layanan Bantuan & Admissions Desk</h3>
                <p>
                  Bagi orang tua yang membutuhkan pendampingan pendaftaran langsung di sekolah, tim admissions siap membantu menggunakan perangkat tablet di front desk.
                </p>
                <div className="pd-contact-hotline-box">
                  <div>
                    <span>WhatsApp Hotline Admissions:</span>
                    <strong>+62 812-3456-7890</strong>
                  </div>
                  <div>
                    <span>Email Admissions:</span>
                    <strong>admissions@permataharapansecondary.sch.id</strong>
                  </div>
                  <div>
                    <span>Jam Layanan Front Desk:</span>
                    <strong>Senin – Jumat: 07.30 – 15.30 WIB | Sabtu: 08.00 – 12.00 WIB</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* =====================================================================
          MODAL 1: ONE-TIME PARENT & FAMILY PROFILE SETUP
      ===================================================================== */}
      {showProfileModal && (
        <div className="pd-modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="pd-modal-box pd-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal-header">
              <div>
                <span className="pd-modal-kicker">Pengaturan Satu Kali untuk Semua Anak</span>
                <h2>Data Orang Tua, Alamat & Berkas Keluarga</h2>
              </div>
              <button type="button" className="pd-modal-close" onClick={() => setShowProfileModal(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveParentProfile} className="pd-modal-body">
              <div className="pd-info-callout">
                Data orang tua, alamat rumah, serta berkas KTP & Kartu Keluarga di bawah ini cukup Anda lengkapi satu kali dan akan otomatis digunakan untuk seluruh pendaftaran putra/putri Anda (saudara kandung).
              </div>

              <h4 className="pd-form-sec-title">1. Data Ayah & Ibu / Wali</h4>
              <div className="pd-form-grid-2">
                <div className="pd-field">
                  <label>Nama Lengkap Ayah *</label>
                  <input
                    type="text"
                    value={profileForm.father_name}
                    onChange={(e) => setProfileForm({ ...profileForm, father_name: e.target.value })}
                    placeholder="Masukkan nama lengkap Ayah"
                    required={!profileForm.mother_name}
                  />
                </div>
                <div className="pd-field">
                  <label>No. Handphone / WhatsApp Ayah *</label>
                  <input
                    type="text"
                    value={profileForm.father_phone}
                    onChange={(e) => setProfileForm({ ...profileForm, father_phone: e.target.value })}
                    placeholder="Contoh: 081234567890"
                  />
                </div>
                <div className="pd-field">
                  <label>Pekerjaan Ayah</label>
                  <input
                    type="text"
                    value={profileForm.father_occupation}
                    onChange={(e) => setProfileForm({ ...profileForm, father_occupation: e.target.value })}
                    placeholder="Pekerjaan / Perusahaan"
                  />
                </div>
                <div className="pd-field">
                  <label>Kewarganegaraan Ayah</label>
                  <select
                    value={profileForm.father_nationality}
                    onChange={(e) => setProfileForm({ ...profileForm, father_nationality: e.target.value })}
                  >
                    <option value="Indonesian (WNI)">Indonesian (WNI)</option>
                    <option value="Expatriate / Foreign (WNA)">Expatriate / Foreign (WNA)</option>
                  </select>
                </div>
              </div>

              <div className="pd-form-grid-2" style={{ marginTop: 14 }}>
                <div className="pd-field">
                  <label>Nama Lengkap Ibu *</label>
                  <input
                    type="text"
                    value={profileForm.mother_name}
                    onChange={(e) => setProfileForm({ ...profileForm, mother_name: e.target.value })}
                    placeholder="Masukkan nama lengkap Ibu"
                    required={!profileForm.father_name}
                  />
                </div>
                <div className="pd-field">
                  <label>No. Handphone / WhatsApp Ibu *</label>
                  <input
                    type="text"
                    value={profileForm.mother_phone}
                    onChange={(e) => setProfileForm({ ...profileForm, mother_phone: e.target.value })}
                    placeholder="Contoh: 081234567890"
                  />
                </div>
                <div className="pd-field">
                  <label>Pekerjaan Ibu</label>
                  <input
                    type="text"
                    value={profileForm.mother_occupation}
                    onChange={(e) => setProfileForm({ ...profileForm, mother_occupation: e.target.value })}
                    placeholder="Pekerjaan / Perusahaan"
                  />
                </div>
                <div className="pd-field">
                  <label>Kewarganegaraan Ibu</label>
                  <select
                    value={profileForm.mother_nationality}
                    onChange={(e) => setProfileForm({ ...profileForm, mother_nationality: e.target.value })}
                  >
                    <option value="Indonesian (WNI)">Indonesian (WNI)</option>
                    <option value="Expatriate / Foreign (WNA)">Expatriate / Foreign (WNA)</option>
                  </select>
                </div>
              </div>

              <h4 className="pd-form-sec-title">2. Alamat Tempat Tinggal Keluarga (Sesuai Formulir Pendaftaran)</h4>
              <div className="pd-field">
                <label>Alamat Lengkap Rumah (Home Address) *</label>
                <textarea
                  rows={2}
                  value={profileForm.home_address}
                  onChange={(e) => setProfileForm({ ...profileForm, home_address: e.target.value })}
                  placeholder="Nama Jalan, Nomor Rumah, RT/RW, Kelurahan, Kecamatan"
                  required
                />
              </div>
              <div className="pd-form-grid-3">
                <div className="pd-field">
                  <label>Kota / Kabupaten</label>
                  <input
                    type="text"
                    value={profileForm.city}
                    onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                  />
                </div>
                <div className="pd-field">
                  <label>Kode Pos</label>
                  <input
                    type="text"
                    value={profileForm.postal_code}
                    onChange={(e) => setProfileForm({ ...profileForm, postal_code: e.target.value })}
                  />
                </div>
                <div className="pd-field">
                  <label>No. Telepon Utama Keluarga *</label>
                  <input
                    type="text"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    required
                  />
                </div>
              </div>

              <h4 className="pd-form-sec-title">3. Unggah Berkas Identitas Keluarga (Berlaku untuk Semua Anak)</h4>
              <div className="pd-form-grid-2">
                <div className="pd-upload-card">
                  <label>KTP Orang Tua (atau Paspor &amp; KITAS bagi WNA)</label>
                  <div className="pd-upload-controls">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.pdf"
                      onChange={(e) =>
                        handleFileUpload(e.target.files?.[0], 'parent_ktp', (url) =>
                          setProfileForm((prev) => ({ ...prev, parent_ktp_url: url }))
                        )
                      }
                    />
                    {uploadingField === 'parent_ktp' && (
                      <div className="pd-upload-progress-wrapper">
                        <div className="pd-upload-progress-header">
                          <span className="pd-upload-progress-text">
                            <RefreshCw size={13} className="pd-spin" />
                            <span>Mengunggah KTP... <strong>{uploadProgress['parent_ktp'] || 0}%</strong></span>
                          </span>
                          <span className="pd-upload-progress-badge">{uploadProgress['parent_ktp'] || 0}%</span>
                        </div>
                        <div className="pd-upload-progress-track">
                          <div className="pd-upload-progress-fill" style={{ width: `${uploadProgress['parent_ktp'] || 0}%` }} />
                        </div>
                      </div>
                    )}
                    {uploadedFilesMeta['parent_ktp'] && uploadingField !== 'parent_ktp' && (
                      <div className="pd-upload-file-chip">
                        <CheckCircle2 size={13} />
                        <span><strong>{uploadedFilesMeta['parent_ktp'].name}</strong> ({uploadedFilesMeta['parent_ktp'].formattedSize})</span>
                      </div>
                    )}
                    {profileForm.parent_ktp_url && (
                      <a
                        href={resolveFileLink(profileForm.parent_ktp_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="pd-file-link"
                      >
                        Lihat File KTP Tersimpan
                      </a>
                    )}
                  </div>
                </div>

                <div className="pd-upload-card">
                  <label>Kartu Keluarga (KK)</label>
                  <div className="pd-upload-controls">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.pdf"
                      onChange={(e) =>
                        handleFileUpload(e.target.files?.[0], 'family_card', (url) =>
                          setProfileForm((prev) => ({ ...prev, family_card_url: url }))
                        )
                      }
                    />
                    {uploadingField === 'family_card' && (
                      <div className="pd-upload-progress-wrapper">
                        <div className="pd-upload-progress-header">
                          <span className="pd-upload-progress-text">
                            <RefreshCw size={13} className="pd-spin" />
                            <span>Mengunggah KK... <strong>{uploadProgress['family_card'] || 0}%</strong></span>
                          </span>
                          <span className="pd-upload-progress-badge">{uploadProgress['family_card'] || 0}%</span>
                        </div>
                        <div className="pd-upload-progress-track">
                          <div className="pd-upload-progress-fill" style={{ width: `${uploadProgress['family_card'] || 0}%` }} />
                        </div>
                      </div>
                    )}
                    {uploadedFilesMeta['family_card'] && uploadingField !== 'family_card' && (
                      <div className="pd-upload-file-chip">
                        <CheckCircle2 size={13} />
                        <span><strong>{uploadedFilesMeta['family_card'].name}</strong> ({uploadedFilesMeta['family_card'].formattedSize})</span>
                      </div>
                    )}
                    {profileForm.family_card_url && (
                      <a
                        href={resolveFileLink(profileForm.family_card_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="pd-file-link"
                      >
                        Lihat File KK Tersimpan
                      </a>
                    )}
                  </div>
                </div>
              </div>

              <div className="pd-modal-footer">
                <button type="button" className="pd-btn-secondary pd-touch-btn" onClick={() => setShowProfileModal(false)}>
                  Batal
                </button>
                <button type="submit" className="pd-btn-primary pd-touch-btn" disabled={savingProfile}>
                  {savingProfile
                    ? 'Menyimpan...'
                    : pendingAddChildAfterProfile
                    ? 'Simpan & Lanjut Daftarkan Anak'
                    : 'Simpan Data Orang Tua'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =====================================================================
          MODAL 2: MULTI-CHILD REGISTRATION (SELECT LEVEL -> FILL FORM)
      ===================================================================== */}
      {showRegisterModal && (
        <div className="pd-modal-overlay" onClick={() => setShowRegisterModal(false)}>
          <div className="pd-modal-box pd-modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal-header">
              <div>
                <span className="pd-modal-kicker">
                  Pendaftaran Calon Siswa {children.length > 0 ? '(Saudara Kandung)' : 'Baru'} — {activePeriod?.period_name || 'TA 2026/2027'}
                </span>
                <h2>
                  {!selectedLevel
                    ? 'Langkah 1: Pilih Jenjang Pendidikan (Level Selection)'
                    : `Langkah 2: Formulir Pendaftaran — ${selectedLevel.level_name}`}
                </h2>
              </div>
              <button
                type="button"
                className="pd-modal-close"
                onClick={() => {
                  setShowRegisterModal(false);
                  setSelectedLevel(null);
                }}
              >
                <X size={20} />
              </button>
            </div>

            {!selectedLevel ? (
              <div className="pd-modal-body">
                <p className="pd-level-intro">
                  Pilih jenjang pendidikan untuk calon siswa. Setiap jenjang menampilkan informasi sub-kelas, kuota kursi periode aktif, serta gambaran tahapan observasi/tes seleksi sesuai standar Sekolah Permata Harapan.
                </p>

                <div className="pd-levels-grid">
                  {portalConfig.levels
                    .filter((lvl) => lvl.is_open && lvl.level_code !== 'sma')
                    .map((lvl) => {
                      const quotaLimit = Number(activePeriod?.quotas?.[lvl.level_code] ?? 30);
                      const quotaUsed = Number(activePeriod?.filled?.[lvl.level_code] ?? 0);
                      const remaining = Math.max(0, quotaLimit - quotaUsed);

                      return (
                        <div
                          key={lvl.level_code}
                          className={`pd-level-select-card ${!lvl.is_open ? 'disabled' : ''}`}
                          onClick={() => handleSelectLevelCard(lvl)}
                        >
                          <div className="pd-level-card-head">
                            <span className="pd-level-tag">{lvl.tag_code}</span>
                            {!lvl.is_open ? (
                              <span className="pd-coming-soon-badge">Coming Soon (Belum Dibuka)</span>
                            ) : (
                              <span className={`pd-seat-badge ${remaining === 0 ? 'rollover' : ''}`}>
                                {remaining > 0 ? `Tersedia ${remaining} Kursi` : 'Auto-Rollover Gelombang Selanjutnya'}
                              </span>
                            )}
                          </div>

                          <h3>{lvl.level_name}</h3>
                          <p className="pd-level-grades">
                            Pilihan Kelas: <strong>{(lvl.available_grades || []).join(', ')}</strong>
                          </p>

                          <div className="pd-level-test-preview">
                            <strong>Tahap Tes & Seleksi ({lvl.assessment_title}):</strong>
                            <p>{lvl.assessment_subjects}</p>
                          </div>

                          {!lvl.is_open && lvl.coming_soon_note && (
                            <p className="pd-coming-soon-text">{lvl.coming_soon_note}</p>
                          )}

                          <div className="pd-level-card-foot">
                            {lvl.is_open ? (
                              <span className="pd-select-cta">
                                Pilih Jenjang Ini <ChevronRight size={15} />
                              </span>
                            ) : (
                              <span className="pd-disabled-cta">Disiapkan untuk Pembukaan Mendatang</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmitChildRegistration} className="pd-modal-body">
                <div className="pd-autofill-bar">
                  <div>
                    <strong>Data Orang Tua & Alamat Otomatis Terhubung:</strong> Ayah:{' '}
                    {parentProfile.father_name || '-'} | Ibu: {parentProfile.mother_name || '-'} | Alamat:{' '}
                    {parentProfile.home_address || '-'}
                  </div>
                  {children.length >= 1 && (
                    <span className="pd-sibling-eligible-tag">
                      Pendaftaran Anak ke-{children.length + 1}
                    </span>
                  )}
                </div>

                <h4 className="pd-form-sec-title">A. Pilihan Kampus, Kelas & Informasi Pendaftaran</h4>
                <div className="pd-form-grid-3">
                  <div className="pd-field">
                    <label>Lokasi Kampus Sekolah Permata Harapan *</label>
                    <select
                      value={childForm.campus_location}
                      onChange={(e) => setChildForm({ ...childForm, campus_location: e.target.value })}
                      required
                    >
                      {portalConfig.campuses.map((c) => (
                        <option key={c.code} value={c.label}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pd-field">
                    <label>Pilihan Kelas / Sub-Grade ({selectedLevel.tag_code}) *</label>
                    <select
                      value={childForm.grade_target}
                      onChange={(e) => setChildForm({ ...childForm, grade_target: e.target.value })}
                      required
                    >
                      {(selectedLevel.available_grades || []).map((gr) => (
                        <option key={gr} value={gr}>
                          {gr}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pd-field">
                    <label>Jalur Pendaftaran</label>
                    <select
                      value={childForm.intake_type}
                      onChange={(e) => setChildForm({ ...childForm, intake_type: e.target.value })}
                    >
                      <option value="Online Website">Online Website</option>
                      <option value="Open House">Open House</option>
                      <option value="Walk in Parent">Walk in Parent</option>
                    </select>
                  </div>
                </div>

                <h4 className="pd-form-sec-title">B. Biodata Lengkap Calon Siswa (Sesuai Formulir Fisik)</h4>
                <div className="pd-form-grid-2">
                  <div className="pd-field">
                    <label>Nama Lengkap Anak (Name of Child) *</label>
                    <input
                      type="text"
                      value={childForm.child_full_name}
                      onChange={(e) => setChildForm({ ...childForm, child_full_name: e.target.value })}
                      placeholder="Sesuai Akta Kelahiran"
                      required
                    />
                  </div>

                  <div className="pd-field">
                    <label>Jenis Kelamin (Gender) *</label>
                    <select
                      value={childForm.gender}
                      onChange={(e) => setChildForm({ ...childForm, gender: e.target.value })}
                      required
                    >
                      <option value="Male">Laki-laki (Male)</option>
                      <option value="Female">Perempuan (Female)</option>
                    </select>
                  </div>

                  <div className="pd-field">
                    <label>Tempat Lahir (Place of Birth) *</label>
                    <input
                      type="text"
                      value={childForm.birth_place}
                      onChange={(e) => setChildForm({ ...childForm, birth_place: e.target.value })}
                      placeholder="Contoh: Jakarta"
                      required
                    />
                  </div>

                  <div className="pd-field">
                    <label>Tanggal Lahir (Date of Birth) *</label>
                    <input
                      type="date"
                      value={childForm.birth_date}
                      onChange={(e) => setChildForm({ ...childForm, birth_date: e.target.value })}
                      required
                    />
                  </div>

                  <div className="pd-field">
                    <label>Kewarganegaraan Anak (Nationality) *</label>
                    <select
                      value={childForm.nationality}
                      onChange={(e) => setChildForm({ ...childForm, nationality: e.target.value })}
                    >
                      <option value="Indonesian (WNI)">Indonesian (WNI)</option>
                      <option value="Expatriate / Foreign (WNA)">Expatriate / Foreign (WNA)</option>
                    </select>
                  </div>

                  <div className="pd-field">
                    <label>
                      Sekolah Asal (Previous School){' '}
                      {['primary', 'secondary'].includes(selectedLevel.level_code) ? '*' : '(Opsional)'}
                    </label>
                    <input
                      type="text"
                      value={childForm.previous_school}
                      onChange={(e) => setChildForm({ ...childForm, previous_school: e.target.value })}
                      placeholder="Nama sekolah sebelumnya (jika ada)"
                      required={selectedLevel.level_code === 'secondary'}
                    />
                  </div>
                </div>

                <h4 className="pd-form-sec-title">C. Unggah Berkas Persyaratan Anak</h4>
                <p className="pd-doc-helper">
                  Berkas KTP Orang Tua dan Kartu Keluarga (KK) sudah otomatis terlampir dari Profil Keluarga Anda. Silakan unggah berkas spesifik anak di bawah ini (Format JPG, PNG, atau PDF):
                </p>

                <div className="pd-form-grid-3">
                  <div className="pd-upload-card">
                    <label>1. Akta Kelahiran Anak (Birth Certificate) *</label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.pdf"
                      onChange={(e) =>
                        handleFileUpload(e.target.files?.[0], 'child_birth_cert', (url) =>
                          setChildForm((prev) => ({
                            ...prev,
                            documents: { ...prev.documents, birth_certificate: url },
                          }))
                        )
                      }
                    />
                    {uploadingField === 'child_birth_cert' && (
                      <div className="pd-upload-progress-wrapper">
                        <div className="pd-upload-progress-header">
                          <span className="pd-upload-progress-text">
                            <RefreshCw size={13} className="pd-spin" />
                            <span>Mengunggah Akta... <strong>{uploadProgress['child_birth_cert'] || 0}%</strong></span>
                          </span>
                          <span className="pd-upload-progress-badge">{uploadProgress['child_birth_cert'] || 0}%</span>
                        </div>
                        <div className="pd-upload-progress-track">
                          <div className="pd-upload-progress-fill" style={{ width: `${uploadProgress['child_birth_cert'] || 0}%` }} />
                        </div>
                      </div>
                    )}
                    {uploadedFilesMeta['child_birth_cert'] && uploadingField !== 'child_birth_cert' && (
                      <div className="pd-upload-file-chip">
                        <CheckCircle2 size={13} />
                        <span><strong>{uploadedFilesMeta['child_birth_cert'].name}</strong> ({uploadedFilesMeta['child_birth_cert'].formattedSize})</span>
                      </div>
                    )}
                    {childForm.documents.birth_certificate && (
                      <span className="pd-uploaded-ok">Akta Kelahiran Terlampir</span>
                    )}
                  </div>

                  <div className="pd-upload-card">
                    <label>2. Pas Foto Terbaru Anak (Ukuran 4x3 / 3x4) *</label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.pdf"
                      onChange={(e) =>
                        handleFileUpload(e.target.files?.[0], 'child_photo', (url) =>
                          setChildForm((prev) => ({
                            ...prev,
                            documents: { ...prev.documents, child_photo: url },
                          }))
                        )
                      }
                    />
                    <span className="pd-sub-text" style={{ fontSize: '0.78rem', color: '#64748b', display: 'block', marginTop: 3 }}>
                      Foto formal/rapi berukuran 4x3 atau 3x4 cm (format JPG, PNG, WEBP, maks 15MB).
                    </span>
                    {uploadingField === 'child_photo' && (
                      <div className="pd-upload-progress-wrapper">
                        <div className="pd-upload-progress-header">
                          <span className="pd-upload-progress-text">
                            <RefreshCw size={13} className="pd-spin" />
                            <span>Mengunggah Pas Foto... <strong>{uploadProgress['child_photo'] || 0}%</strong></span>
                          </span>
                          <span className="pd-upload-progress-badge">{uploadProgress['child_photo'] || 0}%</span>
                        </div>
                        <div className="pd-upload-progress-track">
                          <div className="pd-upload-progress-fill" style={{ width: `${uploadProgress['child_photo'] || 0}%` }} />
                        </div>
                      </div>
                    )}
                    {uploadedFilesMeta['child_photo'] && uploadingField !== 'child_photo' && (
                      <div className="pd-upload-file-chip">
                        <CheckCircle2 size={13} />
                        <span><strong>{uploadedFilesMeta['child_photo'].name}</strong> ({uploadedFilesMeta['child_photo'].formattedSize})</span>
                      </div>
                    )}
                    {childForm.documents.child_photo && <span className="pd-uploaded-ok">Pas Foto Terlampir</span>}
                  </div>

                  <div className="pd-upload-card">
                    <label>
                      3. Rapor Terakhir / Catatan Perkembangan{' '}
                      {['primary', 'secondary'].includes(selectedLevel.level_code) ? '*' : '(Opsional)'}
                    </label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.pdf"
                      onChange={(e) =>
                        handleFileUpload(e.target.files?.[0], 'child_report', (url) =>
                          setChildForm((prev) => ({
                            ...prev,
                            documents: { ...prev.documents, report_card: url },
                          }))
                        )
                      }
                    />
                    {uploadingField === 'child_report' && (
                      <div className="pd-upload-progress-wrapper">
                        <div className="pd-upload-progress-header">
                          <span className="pd-upload-progress-text">
                            <RefreshCw size={13} className="pd-spin" />
                            <span>Mengunggah Rapor... <strong>{uploadProgress['child_report'] || 0}%</strong></span>
                          </span>
                          <span className="pd-upload-progress-badge">{uploadProgress['child_report'] || 0}%</span>
                        </div>
                        <div className="pd-upload-progress-track">
                          <div className="pd-upload-progress-fill" style={{ width: `${uploadProgress['child_report'] || 0}%` }} />
                        </div>
                      </div>
                    )}
                    {uploadedFilesMeta['child_report'] && uploadingField !== 'child_report' && (
                      <div className="pd-upload-file-chip">
                        <CheckCircle2 size={13} />
                        <span><strong>{uploadedFilesMeta['child_report'].name}</strong> ({uploadedFilesMeta['child_report'].formattedSize})</span>
                      </div>
                    )}
                    {childForm.documents.report_card && <span className="pd-uploaded-ok">Rapor Terlampir</span>}
                  </div>
                </div>

                <h4 className="pd-form-sec-title">D. Kode Voucher Diskon / Promo Open House (Opsional)</h4>
                <div className="pd-promo-box">
                  <div className="pd-promo-input-row">
                    <input
                      type="text"
                      value={childForm.promo_code}
                      onChange={(e) =>
                        setChildForm({ ...childForm, promo_code: e.target.value.toUpperCase() })
                      }
                      placeholder="Masukkan kode promo jika ada (Contoh: OPENHOUSE2026)"
                    />
                    <button
                      type="button"
                      className="pd-btn-secondary pd-touch-btn"
                      onClick={handleValidatePromo}
                      disabled={promoCheck.loading || !childForm.promo_code.trim()}
                    >
                      {promoCheck.loading ? 'Memeriksa...' : 'Gunakan Kode'}
                    </button>
                  </div>
                  {promoCheck.valid && promoCheck.info && (
                    <div className="pd-promo-valid">
                      <CheckCircle2 size={16} />
                      <span>
                        Kode <strong>{promoCheck.info.promo_code}</strong> aktif ({promoCheck.info.promo_title}) — Estimasi potongan biaya:{' '}
                        <strong>{formatCurrency(promoCheck.info.estimated_discount_amount)}</strong>
                      </span>
                    </div>
                  )}
                  {promoCheck.error && <div className="pd-promo-error">{promoCheck.error}</div>}
                </div>

                {/* Preview of Assessment Stages for Selected Level */}
                <div className="pd-assessment-info-box">
                  <h5>Informasi Tahapan Selanjutnya Setelah Validasi Berkas ({selectedLevel.level_name}):</h5>
                  <p>
                    <strong>{selectedLevel.assessment_title}</strong> — {selectedLevel.assessment_description}
                  </p>
                </div>

                <div className="pd-modal-footer">
                  <button
                    type="button"
                    className="pd-btn-secondary pd-touch-btn"
                    onClick={() => setSelectedLevel(null)}
                    disabled={submittingChild}
                  >
                    Kembali Pilih Jenjang
                  </button>
                  <button type="submit" className="pd-btn-primary pd-touch-btn" disabled={submittingChild}>
                    {submittingChild ? 'Mengirim Pendaftaran...' : 'Kirim Pendaftaran Calon Siswa'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* =====================================================================
          MODAL 3: CHILD DETAIL, OFFLINE TEST CARD, LoA, INVOICE & PAYMENT UPLOAD
      ===================================================================== */}
      {activeChildDetail && (
        <div className="pd-modal-overlay" onClick={() => setActiveChildDetail(null)}>
          <div className="pd-modal-box pd-modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal-header">
              <div>
                <span className="pd-modal-kicker">
                  Nomor Registrasi: {activeChildDetail.registration_no} | {activeChildDetail.period_name}
                </span>
                <h2>
                  {activeChildDetail.child_full_name} — {activeChildDetail.level_name} ({activeChildDetail.grade_target})
                </h2>
              </div>
              <button type="button" className="pd-modal-close" onClick={() => setActiveChildDetail(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="pd-modal-body">
              {/* Prominent Revision Callout for Payment Revision */}
              {activeChildDetail.status_code === 'payment_revision' && (
                <div className="pd-modal-revision-banner payment">
                  <div className="pd-modal-revision-banner-head">
                    <AlertCircle size={24} className="pd-revision-banner-icon" />
                    <div>
                      <h4>Perhatian: Bukti Pembayaran Perlu Diperbaiki / Ditolak Admin</h4>
                      <p>
                        Admin Admissions telah memeriksa bukti bayar yang Anda kirimkan dan membutuhkan perbaikan data atau unggahan struk baru.
                      </p>
                    </div>
                  </div>
                  <div className="pd-modal-revision-banner-note">
                    <strong>Alasan / Catatan dari Admin Admissions:</strong>
                    <p>&ldquo;{activeChildDetail.admin_note || 'Bukti transfer buram, terpotong, atau nominal belum sesuai. Silakan upload struk baru yang jelas.'}&rdquo;</p>
                  </div>
                  <div className="pd-revision-banner-action">
                    <button
                      type="button"
                      className="pd-btn-warning pd-touch-btn"
                      onClick={() => {
                        const el = document.getElementById('pd-payment-form-anchor');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      <Upload size={15} />
                      <span>Ke Formulir Unggah Ulang Bukti Bayar</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Prominent Revision Callout for Document Revision */}
              {activeChildDetail.status_code === 'document_revision' && (
                <div className="pd-modal-revision-banner document">
                  <div className="pd-modal-revision-banner-head">
                    <AlertCircle size={24} className="pd-revision-banner-icon" />
                    <div>
                      <h4>Perhatian: Berkas Persyaratan Calon Siswa Perlu Diperbaiki</h4>
                      <p>
                        Admin Admissions menemukan beberapa dokumen calon siswa yang belum sesuai, buram, atau tidak terbaca.
                      </p>
                    </div>
                  </div>
                  <div className="pd-modal-revision-banner-note">
                    <strong>Alasan / Catatan dari Admin Admissions:</strong>
                    <p>&ldquo;{activeChildDetail.admin_note || 'Dokumen persyaratan belum lengkap atau buram. Silakan periksa daftar berkas di bawah dan unggah ulang berkas yang diminta.'}&rdquo;</p>
                  </div>
                  <div className="pd-revision-banner-action">
                    <button
                      type="button"
                      className="pd-btn-warning pd-touch-btn"
                      onClick={() => {
                        const el = document.getElementById('pd-docs-section-anchor');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      <Upload size={15} />
                      <span>Ke Daftar Berkas yang Perlu Diperbaiki</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Official Enrolled Student Banner */}
              {activeChildDetail.status_code === 'enrolled' && (
                <div className="pd-official-enrolled-card">
                  <div className="pd-enrolled-header">
                    <div>
                      <span className="pd-enrolled-tag">OFFICIAL STUDENT ENROLLMENT CERTIFICATE</span>
                      <h3>RESMI TERDAFTAR SEBAGAI SISWA SEKOLAH PERMATA HARAPAN</h3>
                      <p>
                        Selamat! Seluruh tahapan seleksi akademik dan administrasi pembayaran untuk{' '}
                        <strong>{activeChildDetail.child_full_name}</strong> telah selesai dan terverifikasi.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="pd-btn-print-action pd-touch-btn"
                      onClick={() => setReceiptModalChild(activeChildDetail)}
                    >
                      <Printer size={16} />
                      <span>Cetak Bukti Siswa &amp; Kwitansi Resmi</span>
                    </button>
                  </div>
                  <div className="pd-enrolled-grid">
                    <div>
                      <span>Nomor Induk Siswa (NIS)</span>
                      <strong>{activeChildDetail.student_id_number}</strong>
                    </div>
                    <div>
                      <span>Nomor Surat Penerimaan (LoA)</span>
                      <strong>{activeChildDetail.loa_number || '-'}</strong>
                    </div>
                    <div>
                      <span>Jenjang & Kelas</span>
                      <strong>
                        {activeChildDetail.level_name} ({activeChildDetail.grade_target})
                      </strong>
                    </div>
                    <div>
                      <span>Lokasi Kampus</span>
                      <strong>{activeChildDetail.campus_location}</strong>
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION 1: OFFLINE TEST SCHEDULE & LEVEL ASSESSMENT DESCRIPTION */}
              {(activeChildDetail.test_schedule || activeChildDetail.assessment_info) && (
                <div className="pd-detail-block">
                  <div className="pd-detail-block-head">
                    <h3>
                      <Calendar size={18} />
                      <span>Tahap Seleksi & Jadwal Tes Offline (Sesuai Jenjang {activeChildDetail.level_name})</span>
                    </h3>
                    {activeChildDetail.test_schedule && (
                      <button
                        type="button"
                        className="pd-btn-test-card pd-touch-btn"
                        onClick={() => setTestCardModalChild(activeChildDetail)}
                      >
                        <Printer size={14} />
                        <span>Cetak Kartu Tes</span>
                      </button>
                    )}
                  </div>

                  {activeChildDetail.test_schedule ? (
                    <div className="pd-test-ticket">
                      {activeChildDetail.test_schedule.auto_slot_rollover_note && (
                        <div className="pd-slot-rollover-banner">
                          <Info size={16} />
                          <span>{activeChildDetail.test_schedule.auto_slot_rollover_note}</span>
                        </div>
                      )}
                      <div className="pd-test-ticket-grid">
                        <div className="pd-ticket-item">
                          <span>Sesi Jadwal Tes</span>
                          <strong>{activeChildDetail.test_schedule.session_name}</strong>
                        </div>
                        <div className="pd-ticket-item">
                          <span>Hari & Tanggal Pelaksanaan</span>
                          <strong>{formatDateId(activeChildDetail.test_schedule.test_date)}</strong>
                        </div>
                        <div className="pd-ticket-item">
                          <span>Waktu Pelaksanaan</span>
                          <strong>{activeChildDetail.test_schedule.test_time}</strong>
                        </div>
                        <div className="pd-ticket-item">
                          <span>Lokasi Kampus & Ruangan</span>
                          <strong>
                            {activeChildDetail.test_schedule.campus_location} —{' '}
                            {activeChildDetail.test_schedule.room_name}
                          </strong>
                        </div>
                      </div>

                      <div className="pd-test-material-box">
                        <h4>
                          Apa Saja yang Diteskan: {activeChildDetail.test_schedule.assessment_title}
                        </h4>
                        <p className="pd-material-subjects">
                          <strong>Materi / Fokus Evaluasi:</strong>{' '}
                          {activeChildDetail.test_schedule.assessment_subjects}
                        </p>
                        <p>{activeChildDetail.test_schedule.assessment_description}</p>
                        <p className="pd-material-prep">
                          <strong>Perlengkapan yang Perlu Dibawa:</strong>{' '}
                          {activeChildDetail.test_schedule.assessment_preparation}
                        </p>
                      </div>

                      {/* Hasil Asesmen Tes Offline & Dokumen Scan PDF di Detail Modal */}
                      {(activeChildDetail.test_score !== null || activeChildDetail.test_result_doc_url || activeChildDetail.test_evaluation_notes) && (
                        <div className="pd-test-score-box" style={{ marginTop: 14 }}>
                          <div className="pd-score-box-header">
                            <div className="pd-score-title-wrap">
                              <Award size={20} className="pd-score-icon" />
                              <div>
                                <h4>Hasil Evaluasi & Skor Tes Seleksi</h4>
                                <span className="pd-score-subtitle">
                                  Hasil penilaian resmi asesmen akademik & observasi dari tim penguji Sekolah Permata Harapan
                                </span>
                              </div>
                            </div>
                            {activeChildDetail.test_score !== null && (
                              <div className="pd-score-badge-circle">
                                <span>Skor Tes</span>
                                <strong>{activeChildDetail.test_score}</strong>
                              </div>
                            )}
                          </div>

                          {activeChildDetail.test_evaluation_notes && (
                            <div className="pd-score-note-row">
                              <strong>Catatan Evaluasi Asesor:</strong>
                              <p>&ldquo;{activeChildDetail.test_evaluation_notes}&rdquo;</p>
                            </div>
                          )}

                          {activeChildDetail.test_result_doc_url && (
                            <div className="pd-score-doc-row">
                              <a
                                href={resolveFileLink(activeChildDetail.test_result_doc_url)}
                                target="_blank"
                                rel="noreferrer"
                                className="pd-btn-download-test-doc pd-touch-btn"
                              >
                                <FileText size={16} />
                                <span>Buka / Unduh Dokumen Hasil Scan Tes (.pdf)</span>
                                <ExternalLink size={13} />
                              </a>
                              <span className="pd-score-doc-hint">
                                Dokumen scan resmi lembar evaluasi tes yang telah dinilai dan diverifikasi.
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="pd-waiting-schedule-box">
                      <p>
                        <strong>Menunggu Validasi Berkas oleh Admin Admissions:</strong> Begitu berkas anak Anda dinyatakan lengkap dan sesuai, jadwal tanggal, jam, dan ruangan tes offline akan otomatis muncul di bagian ini.
                      </p>
                      {activeChildDetail.assessment_info && (
                        <div className="pd-test-material-box" style={{ marginTop: 12 }}>
                          <h4>Gambaran Materi Tes Jenjang Ini: {activeChildDetail.assessment_info.title}</h4>
                          <p>
                            <strong>Materi Seleksi:</strong> {activeChildDetail.assessment_info.subjects}
                          </p>
                          <p>{activeChildDetail.assessment_info.description}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* SECTION 2: TEST RESULT ANNOUNCEMENT, LETTER OF ACCEPTANCE (LoA), INVOICE & PAYMENT */}
              {(activeChildDetail.test_result === 'passed' ||
                activeChildDetail.test_result === 'failed' ||
                activeChildDetail.invoice) && (
                <div className="pd-detail-block">
                  <div className="pd-detail-block-head">
                    <h3>
                      <Award size={18} />
                      <span>Pengumuman Hasil Tes Seleksi & Letter of Acceptance (LoA)</span>
                    </h3>
                  </div>

                  {activeChildDetail.test_result === 'failed' ? (
                    <div className="pd-result-failed-box">
                      <h4>Hasil Evaluasi Seleksi: Belum Memenuhi Kriteria Kelulusan</h4>
                      <p>{activeChildDetail.test_evaluation_notes || activeChildDetail.admin_note}</p>
                    </div>
                  ) : (
                    <>
                      <div className="pd-loa-card">
                        <div className="pd-loa-top">
                          <div>
                            <span className="pd-loa-badge">LETTER OF ACCEPTANCE (LoA)</span>
                            <h4>Nomor Surat: {activeChildDetail.loa_number}</h4>
                            <p>
                              Berdasarkan hasil asesmen dan observasi offline{activeChildDetail.test_score !== null ? ` (Nilai Evaluasi: ${activeChildDetail.test_score})` : ''}, calon siswa{' '}
                              <strong>{activeChildDetail.child_full_name}</strong> dinyatakan{' '}
                              <strong>LULUS SELEKSI</strong> pada jenjang{' '}
                              <strong>
                                {activeChildDetail.level_name} ({activeChildDetail.grade_target})
                              </strong>.
                            </p>
                            {activeChildDetail.test_evaluation_notes && (
                              <p className="pd-eval-note">
                                Catatan Tim Akademik: &ldquo;{activeChildDetail.test_evaluation_notes}&rdquo;
                              </p>
                            )}
                            <div style={{ marginTop: 12 }}>
                              <button
                                type="button"
                                className="pd-btn-loa-primary pd-touch-btn"
                                onClick={() => setLoaModalChild(activeChildDetail)}
                              >
                                <Award size={15} />
                                <span>Buka Letter of Acceptance (LoA) Resmi</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* INVOICE RINCIAN BIAYA SESUAI FORMULIR FISIK */}
                      {activeChildDetail.invoice && (
                        <div className="pd-invoice-card">
                          <div className="pd-invoice-head">
                            <div>
                              <h4>Rincian Biaya Pendaftaran Siswa Baru (Official Fee Breakdown)</h4>
                              <p>Nomor Tagihan: {activeChildDetail.invoice.invoice_no}</p>
                            </div>
                            <div className="pd-payment-code-box">
                              <span>Kode Pembayaran Unik</span>
                              <strong>{activeChildDetail.invoice.payment_code}</strong>
                            </div>
                          </div>

                          <table className="pd-invoice-table">
                            <thead>
                              <tr>
                                <th>Komponen Biaya (Sesuai Formulir Pendaftaran)</th>
                                <th>Keterangan / Jatuh Tempo</th>
                                <th className="text-right">Nominal</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Number(activeChildDetail.invoice.registration_fee) > 0 && (
                                <tr>
                                  <td>Registration Fee</td>
                                  <td>Biaya Pendaftaran Awal</td>
                                  <td className="text-right">
                                    {formatCurrency(activeChildDetail.invoice.registration_fee)}
                                  </td>
                                </tr>
                              )}
                              {Number(activeChildDetail.invoice.test_reg_form_fee) > 0 && (
                                <tr>
                                  <td>Test / Reg. Form Fee</td>
                                  <td>Biaya Tes & Formulir</td>
                                  <td className="text-right">
                                    {formatCurrency(activeChildDetail.invoice.test_reg_form_fee)}
                                  </td>
                                </tr>
                              )}
                              {Number(activeChildDetail.invoice.admission_fee) > 0 && (
                                <tr>
                                  <td>Admission Fee</td>
                                  <td>Saat Daftar Ulang</td>
                                  <td className="text-right">
                                    {formatCurrency(activeChildDetail.invoice.admission_fee)}
                                  </td>
                                </tr>
                              )}
                              <tr>
                                <td>
                                  <strong>Development Fee</strong>
                                  {Number(activeChildDetail.invoice.df_1_amount) > 0 && (
                                    <div className="pd-df-installments">
                                      Opsi Termin: DF 1: {formatCurrency(activeChildDetail.invoice.df_1_amount)} | DF 2:{' '}
                                      {formatCurrency(activeChildDetail.invoice.df_2_amount)} | DF 3:{' '}
                                      {formatCurrency(activeChildDetail.invoice.df_3_amount)}
                                      {Number(activeChildDetail.invoice.df_4_amount) > 0 ? ` | DF 4: ${formatCurrency(activeChildDetail.invoice.df_4_amount)}` : ''}
                                    </div>
                                  )}
                                </td>
                                <td>{activeChildDetail.invoice.df_due_notes || '-'}</td>
                                <td className="text-right">
                                  {formatCurrency(activeChildDetail.invoice.development_fee)}
                                </td>
                              </tr>
                              {Number(activeChildDetail.invoice.books_activity_fee) > 0 && (
                                <tr>
                                  <td>Books & Activity Fee</td>
                                  <td>{activeChildDetail.invoice.books_due_notes || '-'}</td>
                                  <td className="text-right">
                                    {formatCurrency(activeChildDetail.invoice.books_activity_fee)}
                                  </td>
                                </tr>
                              )}
                              <tr>
                                <td>Tuition Fee (SPP Bulan Pertama)</td>
                                <td>Bulanan</td>
                                <td className="text-right">
                                  {formatCurrency(activeChildDetail.invoice.tuition_fee)}
                                </td>
                              </tr>
                              {Number(activeChildDetail.invoice.discount_amount) > 0 && (
                                <tr className="pd-discount-row">
                                  <td>
                                    Potongan Diskon ({activeChildDetail.invoice.discount_label || 'Promo Voucher'})
                                  </td>
                                  <td>Potongan Resmi</td>
                                  <td className="text-right">
                                    - {formatCurrency(activeChildDetail.invoice.discount_amount)}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                            <tfoot>
                              <tr>
                                <th colSpan={2}>Total Biaya yang Harus Diselesaikan</th>
                                <th className="text-right pd-total-amount">
                                  {formatCurrency(activeChildDetail.invoice.total_payable)}
                                </th>
                              </tr>
                            </tfoot>
                          </table>

                          <div className="pd-bank-info-bar">
                            <div>
                              <span>Rekening Tujuan Transfer Resmi Sekolah:</span>
                              <strong>
                                {activeChildDetail.invoice.bank_name} — No. Rek:{' '}
                                {activeChildDetail.invoice.bank_account_number} a/n{' '}
                                {activeChildDetail.invoice.bank_account_holder}
                              </strong>
                            </div>
                            <span className="pd-non-refundable">{activeChildDetail.invoice.disclaimer}</span>
                          </div>

                          {/* PAYMENT METHOD SELECTION & PROOF UPLOAD */}
                          {activeChildDetail.status_code !== 'enrolled' && (
                            activeChildDetail.level_fee_approval_status && activeChildDetail.level_fee_approval_status !== 'approved' ? (
                              <div className="pd-pending-fee-approval-notice" style={{ marginTop: 14 }}>
                                <Clock size={20} color="#d97706" />
                                <div>
                                  <strong>Tarif Biaya Masih Menunggu Persetujuan Direktur Sekolah (School Director)</strong>
                                  <p>
                                    Rincian tagihan resmi untuk jenjang ini sedang ditinjau oleh Direktur Sekolah. Anda belum dapat mengirimkan konfirmasi pembayaran hingga tarif disahkan. Silakan periksa kembali beberapa saat lagi.
                                  </p>
                                </div>
                              </div>
                            ) : (
                            <form onSubmit={handleSubmitPayment} className="pd-payment-form" id="pd-payment-form-anchor">
                              <h4>Pilih Metode Penyelesaian Pembayaran & Unggah Bukti Bayar</h4>

                              {activeChildDetail.status_code === 'payment_revision' && (
                                <div className="pd-payment-revision-callout">
                                  <div className="pd-payment-revision-callout-header">
                                    <AlertCircle size={18} />
                                    <strong>Bukti Pembayaran Sebelumnya Perlu Direvisi</strong>
                                  </div>
                                  <p className="pd-payment-revision-callout-note">
                                    Catatan Admin Admissions: &ldquo;{activeChildDetail.admin_note}&rdquo;
                                  </p>
                                  <span className="pd-payment-revision-callout-guide">
                                    Silakan periksa kembali nominal transfer ({formatCurrency(activeChildDetail.invoice.total_payable)}), pastikan foto bukti transfer jelas & tidak terpotong, serta gunakan format berita transfer di bawah.
                                  </span>
                                </div>
                              )}

                              <div className="pd-payment-methods">
                                <label
                                  className={`pd-method-card ${
                                    paymentForm.payment_method === 'transfer_bank' ? 'active' : ''
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name="payment_method"
                                    value="transfer_bank"
                                    checked={paymentForm.payment_method === 'transfer_bank'}
                                    onChange={(e) =>
                                      setPaymentForm({ ...paymentForm, payment_method: e.target.value })
                                    }
                                  />
                                  <div>
                                    <strong>Transfer Rekening Sekolah / Kode Bayar</strong>
                                    <span>
                                      Transfer melalui Bank/M-Banking lalu unggah foto/PDF bukti bayar untuk divalidasi Admin.
                                    </span>
                                  </div>
                                </label>

                                <label
                                  className={`pd-method-card ${
                                    paymentForm.payment_method === 'direct_school' ? 'active' : ''
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name="payment_method"
                                    value="direct_school"
                                    checked={paymentForm.payment_method === 'direct_school'}
                                    onChange={(e) =>
                                      setPaymentForm({ ...paymentForm, payment_method: e.target.value })
                                    }
                                  />
                                  <div>
                                    <strong>Bayar Langsung ke Sekolah (Cash / Debit / Card)</strong>
                                    <span>
                                      Tunjukkan Kode Bayar ({activeChildDetail.invoice.payment_code}) di Kasir Sekolah Permata Harapan.
                                    </span>
                                  </div>
                                </label>
                              </div>

                              <div className="pd-form-grid-2" style={{ marginTop: 14 }}>
                                <div className="pd-field">
                                  <label>Nominal yang Dibayarkan (Rp) *</label>
                                  <input
                                    type="number"
                                    value={paymentForm.payment_amount_paid}
                                    onChange={(e) =>
                                      setPaymentForm({ ...paymentForm, payment_amount_paid: e.target.value })
                                    }
                                    required
                                  />
                                </div>

                                <div className="pd-upload-card">
                                  <label>
                                    {paymentForm.payment_method === 'direct_school'
                                      ? 'Unggah Foto Kwitansi Kasir Sekolah (Opsional)'
                                      : 'Unggah Bukti Transfer Pembayaran (JPG/PNG/PDF) *'}
                                  </label>
                                  <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.webp,.pdf"
                                    onChange={(e) =>
                                      handleFileUpload(e.target.files?.[0], 'payment_proof', (url) =>
                                        setPaymentForm((prev) => ({ ...prev, payment_proof_url: url }))
                                      )
                                    }
                                  />
                                  {uploadingField === 'payment_proof' && (
                                    <div className="pd-upload-progress-wrapper">
                                      <div className="pd-upload-progress-header">
                                        <span className="pd-upload-progress-text">
                                          <RefreshCw size={13} className="pd-spin" />
                                          <span>Mengunggah Bukti Bayar... <strong>{uploadProgress['payment_proof'] || 0}%</strong></span>
                                        </span>
                                        <span className="pd-upload-progress-badge">{uploadProgress['payment_proof'] || 0}%</span>
                                      </div>
                                      <div className="pd-upload-progress-track">
                                        <div className="pd-upload-progress-fill" style={{ width: `${uploadProgress['payment_proof'] || 0}%` }} />
                                      </div>
                                    </div>
                                  )}
                                  {uploadedFilesMeta['payment_proof'] && uploadingField !== 'payment_proof' && (
                                    <div className="pd-upload-file-chip">
                                      <CheckCircle2 size={13} />
                                      <span><strong>{uploadedFilesMeta['payment_proof'].name}</strong> ({uploadedFilesMeta['payment_proof'].formattedSize})</span>
                                    </div>
                                  )}
                                  {paymentForm.payment_proof_url && (
                                    <a
                                      href={resolveFileLink(paymentForm.payment_proof_url)}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="pd-file-link"
                                    >
                                      Lihat Bukti Bayar Terlampir
                                    </a>
                                  )}
                                </div>
                              </div>

                              {paymentForm.payment_method === 'transfer_bank' ? (
                                <div className="pd-transfer-note-builder">
                                  <div className="pd-field-helper-title">
                                    <strong>Format Berita Transfer Resmi: [Nama Pengirim]_[Bank]_[Nama Anak]</strong>
                                    <span>Isi identitas rekening pengirim Anda agar mutasi transfer otomatis teridentifikasi oleh sistem.</span>
                                  </div>

                                  <div className="pd-form-grid-2">
                                    <div className="pd-field">
                                      <label>Nama Pemilik Rekening Pengirim *</label>
                                      <input
                                        type="text"
                                        value={paymentForm.sender_account_name}
                                        onChange={(e) =>
                                          setPaymentForm({
                                            ...paymentForm,
                                            sender_account_name: e.target.value,
                                          })
                                        }
                                        placeholder="Contoh: Ananda Ivana A"
                                        required
                                      />
                                      <span className="pd-field-hint">Nama pemilik buku tabungan/rekening pengirim</span>
                                    </div>

                                    <div className="pd-field">
                                      <label>Bank Asal Pengirim *</label>
                                      <input
                                        type="text"
                                        value={paymentForm.sender_bank_name}
                                        onChange={(e) =>
                                          setPaymentForm({
                                            ...paymentForm,
                                            sender_bank_name: e.target.value,
                                          })
                                        }
                                        placeholder="Contoh: Bank BCA / Mandiri / BNI"
                                        required
                                      />
                                      <span className="pd-field-hint">Nama bank yang Anda gunakan untuk transfer</span>
                                    </div>
                                  </div>

                                  <div className="pd-format-guide-box">
                                    <div className="pd-format-preview-label">Standar Catatan Berita Transfer Otomatis:</div>
                                    <div className="pd-format-preview-chip">
                                      <code>
                                        {(paymentForm.sender_account_name || 'Nama Rekening Pengirim').trim()}_{paymentForm.sender_bank_name || 'Bank'}_{activeChildDetail.child_full_name}
                                        {paymentForm.additional_notes ? ` (${paymentForm.additional_notes})` : ''}
                                      </code>
                                    </div>
                                    <div className="pd-format-helper">
                                      Tip: Saat transfer di M-Banking / ATM, tulis teks format di atas pada kolom Catatan / Berita Transfer.
                                    </div>
                                  </div>

                                  <div className="pd-field" style={{ marginTop: 12 }}>
                                    <label>Catatan Tambahan (Opsional)</label>
                                    <input
                                      type="text"
                                      value={paymentForm.additional_notes}
                                      onChange={(e) =>
                                        setPaymentForm({
                                          ...paymentForm,
                                          additional_notes: e.target.value,
                                        })
                                      }
                                      placeholder="Contoh: DF Termin 1 / Cicilan pertama"
                                    />
                                  </div>
                                </div>
                              ) : (
                                <div className="pd-field" style={{ marginTop: 10 }}>
                                  <label>Catatan Pembayaran di Kasir Sekolah</label>
                                  <input
                                    type="text"
                                    value={paymentForm.payment_notes}
                                    onChange={(e) =>
                                      setPaymentForm({ ...paymentForm, payment_notes: e.target.value })
                                    }
                                    placeholder="Contoh: Pembayaran di kasir sekolah a/n Orang Tua"
                                  />
                                </div>
                              )}

                              <div className="pd-payment-submit-row">
                                <button
                                  type="submit"
                                  className="pd-btn-primary pd-touch-btn"
                                  disabled={submittingPayment}
                                >
                                  {submittingPayment
                                    ? 'Mengirim Konfirmasi...'
                                    : paymentForm.payment_method === 'direct_school'
                                    ? 'Konfirmasi Pembayaran Langsung di Sekolah'
                                    : 'Kirim Bukti Pembayaran untuk Diverifikasi Admin'}
                                </button>
                              </div>
                            </form>
                          )
                        )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* SECTION 3: CHILD BIODATA & DOCUMENT VALIDATION STATUS */}
              <div className="pd-detail-block" id="pd-docs-section-anchor">
                <div className="pd-detail-block-head">
                  <h3>
                    <FileText size={18} />
                    <span>Biodata Anak & Status Validasi Berkas Persyaratan</span>
                  </h3>
                </div>

                <div className="pd-biodata-grid">
                  <div>
                    <span>Nama Lengkap Anak:</span>
                    <strong>{activeChildDetail.child_full_name}</strong>
                  </div>
                  <div>
                    <span>Tempat, Tanggal Lahir:</span>
                    <strong>
                      {activeChildDetail.birth_place}, {formatDateId(activeChildDetail.birth_date)}
                    </strong>
                  </div>
                  <div>
                    <span>Jenis Kelamin:</span>
                    <strong>{activeChildDetail.gender}</strong>
                  </div>
                  <div>
                    <span>Kewarganegaraan:</span>
                    <strong>{activeChildDetail.nationality}</strong>
                  </div>
                  <div>
                    <span>Sekolah Asal:</span>
                    <strong>{activeChildDetail.previous_school || '-'}</strong>
                  </div>
                  <div>
                    <span>Lokasi Kampus:</span>
                    <strong>{activeChildDetail.campus_location}</strong>
                  </div>
                </div>

                <h4 style={{ marginTop: 18, marginBottom: 10 }}>Daftar Berkas Persyaratan:</h4>
                <form onSubmit={handleResubmitDocuments}>
                  <div className="pd-docs-list">
                    {Object.entries(activeChildDetail.documents || {}).map(([docKey, docObj]) => {
                      const docStatus = docObj?.status || 'pending';
                      const fieldKey = `rev_${docKey}`;
                      const isUploading = uploadingField === fieldKey;
                      const progress = uploadProgress[fieldKey] || 0;
                      const fileMeta = uploadedFilesMeta[fieldKey];
                      const hasReplaced = Boolean(revisionDocs[docKey]);
                      const currentUrl = revisionDocs[docKey] || docObj?.url || '';

                      return (
                        <div
                          key={docKey}
                          className={`pd-doc-row status-${docStatus} ${hasReplaced ? 'pd-doc-row-replaced' : ''} ${isUploading ? 'pd-doc-row-uploading' : ''}`}
                        >
                          <div className="pd-doc-info">
                            <div className="pd-doc-title-row">
                              <strong>{docObj?.label || docKey}</strong>
                              {hasReplaced ? (
                                <span className="pd-doc-status-pill updated">
                                  <CheckCircle2 size={12} />
                                  <span>File Baru Siap Dikirim</span>
                                </span>
                              ) : (
                                <span className={`pd-doc-status-pill ${docStatus}`}>
                                  {docStatus === 'valid'
                                    ? 'Valid / Sesuai'
                                    : docStatus === 'revision'
                                    ? 'Perlu Diunggah Ulang'
                                    : 'Menunggu Pemeriksaan'}
                                </span>
                              )}
                            </div>

                            {docStatus === 'revision' && !hasReplaced && (
                              <p className="pd-doc-note revision">
                                <strong>Catatan Perbaikan dari Admin:</strong> &ldquo;{docObj?.note || 'Berkas buram atau tidak sesuai. Silakan upload ulang berkas yang jelas.'}&rdquo;
                              </p>
                            )}

                            {docStatus !== 'revision' && docObj?.note && (
                              <p className="pd-doc-note">Catatan: {docObj.note}</p>
                            )}

                            {/* Actively uploading indicator with progress bar & percentage */}
                            {isUploading && (
                              <div className="pd-upload-progress-wrapper">
                                <div className="pd-upload-progress-header">
                                  <span className="pd-upload-progress-text">
                                    <RefreshCw size={13} className="pd-spin" />
                                    <span>Sedang mengunggah file... <strong>{progress}%</strong></span>
                                  </span>
                                  <span className="pd-upload-progress-badge">{progress}%</span>
                                </div>
                                <div className="pd-upload-progress-track">
                                  <div
                                    className="pd-upload-progress-fill"
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Newly replaced file confirmation card */}
                            {hasReplaced && fileMeta && !isUploading && (
                              <div className="pd-doc-replaced-card">
                                <div className="pd-doc-replaced-left">
                                  <div className="pd-doc-replaced-icon">
                                    <CheckCircle2 size={16} />
                                  </div>
                                  <div>
                                    <div className="pd-doc-replaced-name">
                                      <span className="pd-doc-replaced-lbl">File Baru:</span>{' '}
                                      <strong>{fileMeta.name}</strong>
                                    </div>
                                    <div className="pd-doc-replaced-details">
                                      <span>{fileMeta.formattedSize}</span>
                                      <span>&bull;</span>
                                      <span>Diunggah {fileMeta.time} WIB</span>
                                    </div>
                                  </div>
                                </div>
                                <span className="pd-doc-replaced-tag">Berhasil Diunggah</span>
                              </div>
                            )}
                          </div>

                          <div className="pd-doc-actions">
                            {currentUrl ? (
                              <a
                                href={resolveFileLink(currentUrl)}
                                target="_blank"
                                rel="noreferrer"
                                className={`pd-file-link ${hasReplaced ? 'pd-file-link-new' : ''}`}
                              >
                                {hasReplaced ? (
                                  <>
                                    <Eye size={13} />
                                    <span>Lihat File Baru</span>
                                    <ExternalLink size={12} />
                                  </>
                                ) : (
                                  <>
                                    <span>Lihat Berkas</span>
                                    <ExternalLink size={13} />
                                  </>
                                )}
                              </a>
                            ) : (
                              <span className="pd-no-file">Belum ada file</span>
                            )}

                            {activeChildDetail.status_code !== 'enrolled' && (
                              isUploading ? (
                                <button type="button" className="pd-reupload-btn pd-reupload-btn-loading" disabled>
                                  <RefreshCw size={12} className="pd-spin" />
                                  <span>{progress}%</span>
                                </button>
                              ) : (
                                <label className={`pd-reupload-btn pd-touch-btn ${hasReplaced ? 'pd-reupload-btn-replaced' : ''}`}>
                                  <span>{hasReplaced ? 'Ganti Lagi' : (currentUrl ? 'Ganti File' : 'Upload File')}</span>
                                  <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.webp,.pdf"
                                    style={{ display: 'none' }}
                                    onChange={(e) =>
                                      handleFileUpload(e.target.files?.[0], fieldKey, (url) =>
                                        setRevisionDocs((prev) => ({ ...prev, [docKey]: url }))
                                      )
                                    }
                                  />
                                </label>
                              )
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {Object.keys(revisionDocs).length > 0 && (
                    <div className="pd-resubmit-alert-card">
                      <div className="pd-resubmit-alert-content">
                        <div className="pd-resubmit-alert-icon">
                          <CheckCircle2 size={18} />
                        </div>
                        <div>
                          <strong style={{ display: 'block', fontSize: '0.88rem', color: '#0f172a' }}>
                            {Object.keys(revisionDocs).length} Berkas Telah Berhasil Diganti &amp; Siap Dikirim
                          </strong>
                          <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#475569' }}>
                            File baru telah tersimpan di server. Klik tombol di samping untuk mengirimkan revisi ke Admin Admissions.
                          </p>
                        </div>
                      </div>
                      <button
                        type="submit"
                        className="pd-btn-primary pd-touch-btn pd-resubmit-btn-ready"
                        disabled={submittingRevision || Boolean(uploadingField)}
                      >
                        {submittingRevision ? (
                          <>
                            <RefreshCw size={14} className="pd-spin" />
                            <span>Menyimpan Berkas...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle2 size={15} />
                            <span>Simpan &amp; Kirim Ulang Berkas ({Object.keys(revisionDocs).length})</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================================
          FORMAL ENGLISH ADMISSIONS DOCUMENTS (SINGLE-PAGE A4 PRINT READY)
      ===================================================================== */}
      {loaModalChild && (
        <LetterOfAcceptanceModal
          app={loaModalChild}
          onClose={() => setLoaModalChild(null)}
        />
      )}

      {enrollmentCertChild && (
        <OfficialEnrollmentCertificateModal
          app={enrollmentCertChild}
          onClose={() => setEnrollmentCertChild(null)}
        />
      )}

      {receiptModalChild && (
        <OfficialPaymentReceiptModal
          app={receiptModalChild}
          onClose={() => setReceiptModalChild(null)}
        />
      )}

      {testCardModalChild && (
        <ExaminationPassModal
          app={testCardModalChild}
          onClose={() => setTestCardModalChild(null)}
        />
      )}

      {/* Floating High-Visibility Modern Notification Toast */}
      <ModernAdmissionsToast
        toast={message}
        onClose={() => setMessage(null)}
        duration={15000}
      />
    </DashboardLayout>
  );

  // Helper renderer for child card
  function renderChildCard(child) {
    const st = getStatusMeta(child.status_code);
    const steps = [
      { num: 1, title: 'Data & Berkas' },
      { num: 2, title: 'Validasi Admin' },
      { num: 3, title: 'Tes Offline' },
      { num: 4, title: 'Hasil & LoA' },
      { num: 5, title: 'Pembayaran' },
      { num: 6, title: 'Resmi Siswa' },
    ];

    return (
      <div key={child.id} className="pd-child-card">
        <div className="pd-child-card-top">
          <div>
            <div className="pd-child-badges">
              <span className="pd-reg-no">{child.registration_no}</span>
              <span className="pd-level-pill">
                {child.level_name} — {child.grade_target}
              </span>
              {child.sibling_order > 1 && (
                <span className="pd-sibling-pill">Saudara Kandung (Sibling)</span>
              )}
            </div>
            <h3 className="pd-child-name">{child.child_full_name}</h3>
            <p className="pd-child-sub">
              <MapPin size={14} />
              <span>Kampus: {child.campus_location}</span>
              <span className="pd-dot">|</span>
              <span>Periode: {child.period_name}</span>
            </p>
          </div>

          <div className="pd-child-status-col">
            <span className={`pd-status-badge tone-${st.tone}`}>{st.label}</span>
            {child.student_id_number && (
              <span className="pd-nis-badge">NIS: {child.student_id_number}</span>
            )}
          </div>
        </div>

        {child.is_rollover_period && child.rollover_reason && (
          <div className="pd-rollover-notice">
            <AlertCircle size={15} />
            <span>{child.rollover_reason}</span>
          </div>
        )}

        {/* 6-Step Progress Bar */}
        <div className="pd-stepper">
          {steps.map((s) => {
            const isDone = st.step > s.num || child.status_code === 'enrolled';
            const isCurrent = st.step === s.num && child.status_code !== 'enrolled';
            return (
              <div
                key={s.num}
                className={`pd-step-item ${isDone ? 'done' : ''} ${isCurrent ? 'current' : ''}`}
              >
                <div className="pd-step-circle">{isDone ? '✓' : s.num}</div>
                <span className="pd-step-label">{s.title}</span>
              </div>
            );
          })}
        </div>

        {/* Quick Stage Highlights on Card */}
        <div className="pd-card-highlights">
          {child.status_code === 'test_scheduled' && child.test_schedule && (
            <div className="pd-highlight-box purple">
              <div className="pd-highlight-title">
                <Calendar size={16} />
                <strong>Jadwal Tes & Observasi Offline: {child.test_schedule.session_name}</strong>
              </div>
              <p>
                Tanggal: <strong>{formatDateId(child.test_schedule.test_date)}</strong> | Jam:{' '}
                <strong>{child.test_schedule.test_time}</strong> | Lokasi:{' '}
                <strong>
                  {child.test_schedule.campus_location} ({child.test_schedule.room_name})
                </strong>
              </p>
              <p className="pd-highlight-sub">
                Materi Seleksi ({child.test_schedule.assessment_title}): {child.test_schedule.assessment_subjects}
              </p>
            </div>
          )}

          {child.status_code === 'payment_revision' && (
            <div className="pd-highlight-box red-warning">
              <div className="pd-highlight-title">
                <AlertCircle size={18} />
                <strong>Perhatian: Bukti Pembayaran Perlu Diperbaiki / Ditolak Admin</strong>
              </div>
              <p className="pd-highlight-admin-note">
                <strong>Alasan / Catatan Admin:</strong> &ldquo;{child.admin_note || 'Bukti transfer buram atau nominal belum sesuai. Silakan upload struk baru.'}&rdquo;
              </p>
              <div className="pd-highlight-action-row">
                <span className="pd-highlight-badge">Tindakan Diperlukan: Unggah Bukti Bayar Baru</span>
              </div>
            </div>
          )}

          {child.status_code === 'document_revision' && (
            <div className="pd-highlight-box orange-warning">
              <div className="pd-highlight-title">
                <AlertCircle size={18} />
                <strong>Perhatian: Berkas Persyaratan Calon Siswa Perlu Diperbaiki</strong>
              </div>
              <p className="pd-highlight-admin-note">
                <strong>Alasan / Catatan Admin:</strong> &ldquo;{child.admin_note || 'Ada berkas yang buram atau belum lengkap. Silakan periksa dan unggah ulang.'}&rdquo;
              </p>
              <div className="pd-highlight-action-row">
                <span className="pd-highlight-badge">Tindakan Diperlukan: Periksa & Upload Ulang Berkas</span>
              </div>
            </div>
          )}

          {child.status_code === 'payment_submitted' && (
            <div className="pd-highlight-box blue-info">
              <div className="pd-highlight-title">
                <Clock size={16} />
                <strong>Bukti Pembayaran Sedang Diverifikasi oleh Admin Admissions</strong>
              </div>
              <p>
                Konfirmasi bukti pembayaran telah terkirim. Admin sedang memvalidasi mutasi dan bukti transfer Anda.
              </p>
            </div>
          )}

          {child.status_code === 'test_passed' && child.invoice && (
            <div className="pd-highlight-box teal">
              <div className="pd-highlight-title">
                <Award size={16} />
                <strong>
                  Lulus Seleksi! Letter of Acceptance ({child.loa_number}) & Rincian Biaya Tersedia
                </strong>
              </div>
              <p>
                Kode Pembayaran: <strong>{child.invoice.payment_code}</strong> | Total Tagihan:{' '}
                <strong>{formatCurrency(child.invoice.total_payable)}</strong> | Rekening:{' '}
                <strong>
                  {child.invoice.bank_name} - {child.invoice.bank_account_number}
                </strong>
              </p>
            </div>
          )}

          {child.status_code === 'enrolled' && (
            <div className="pd-highlight-box green">
              <div className="pd-highlight-title">
                <ShieldCheck size={16} />
                <strong>
                  Resmi Dinyatakan Siswa Sekolah Permata Harapan (Nomor Induk: {child.student_id_number})
                </strong>
              </div>
              <p>{child.admin_note}</p>
            </div>
          )}
        </div>

        <div className="pd-child-card-footer">
          <span className="pd-date-meta">Terdaftar pada {formatDateId(child.created_at)}</span>
          <button
            type="button"
            className="pd-btn-detail pd-touch-btn"
            onClick={() => handleOpenChildDetail(child)}
          >
            <span>
              {child.status_code === 'payment_revision'
                ? 'Perbaiki & Upload Ulang Bukti Bayar'
                : child.status_code === 'document_revision'
                ? 'Periksa & Upload Ulang Berkas'
                : child.status_code === 'test_passed'
                ? 'Lihat LoA, Rincian Biaya & Upload Bukti Bayar'
                : child.status_code === 'test_scheduled'
                ? 'Lihat Detail Jadwal & Materi Tes Offline'
                : child.status_code === 'payment_submitted'
                ? 'Lihat Status Verifikasi Pembayaran'
                : 'Lihat Detail Status & Berkas Anak'}
            </span>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  }
}

export default ParentDashboard;
